import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { View, UserSettings, Child, Class, Student, Homework, Assignment, Submission, Notification } from '../types';
import { getDashboardGreeting, getDashboardSubtitle } from '../utils/dashboardGreetings';
import Card from './common/Card';
import Spinner from './common/Spinner';
import { UserGroupIcon, UserCircleIcon, UserPlusIcon, PencilIcon, TrashIcon, XMarkIcon, ClipboardDocumentListIcon, ChartBarIcon, ClockIcon, CheckBadgeIcon, ChatBubbleBottomCenterTextIcon, PhoneIcon, PlusIcon, MagnifyingGlassIcon, BookOpenIcon, AcademicCapIcon, CheckIcon, InformationCircleIcon, ArrowPathIcon, ExclamationTriangleIcon, SparklesIcon, MegaphoneIcon, ChevronRightIcon, ClipboardDocumentCheckIcon, UploadIcon, PhotoIcon, BellIcon, Square2StackIcon, DocumentDuplicateIcon, FlagIcon, WalletIcon } from './common/Icons';
import HelpOnboarding, { HelpStep } from './common/HelpOnboarding';
import useDraggable from '../hooks/useDraggable';
import { getUserLimits, checkLimit } from '../utils/limitUtils';
import { isSubmissionGraded } from '../utils/assignmentUtils';
import { createJoinRequest, findClassByCode, getClassStudents, checkJoinEligibility, uploadImageResource, formatAssetUrl } from '../services/apiService';
import { APP_MODE, EDU_CONFIG } from '../config';

const parentHelpSteps: HelpStep[] = [
    {
        title: 'Theo dõi Học tập',
        description: 'Xem biểu đồ điểm trung bình và tỉ lệ chuyên cần để nắm bắt tình hình học tập thực tế của các con.',
        icon: <ChartBarIcon className="h-12 w-12 text-teal-600" />
    },
    {
        title: 'Báo cáo & Nhận xét',
        description: 'Xem chi tiết bài làm, bài chấm và nhận xét của giáo viên cho từng bài tập của con.',
        icon: <ClipboardDocumentListIcon className="h-12 w-12 text-teal-600" />
    },
    {
        title: 'Thông báo Tức thì',
        description: 'Nhận thông báo ngay khi có bài tập mới, điểm số hoặc lời nhắn quan trọng từ trường học.',
        icon: <BellIcon className="h-12 w-12 text-teal-600" />
    },
    {
        title: 'Kết nối Giáo viên',
        description: 'Trao đổi trực tiếp với giáo viên qua Zalo hoặc Hotline để phối hợp giáo dục con tốt nhất.',
        icon: <ChatBubbleBottomCenterTextIcon className="h-12 w-12 text-teal-600" />
    }
];

const calculateAge = (dob: string) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const ProgressRing = ({ progress, size = 48, mdSize, strokeWidth = 4, color = 'text-teal-600' }: { progress: number; size?: number; mdSize?: number; strokeWidth?: number; color?: string }) => {
    // Determine size based on viewport
    const [currentSize, setCurrentSize] = useState(size);
    
    useEffect(() => {
        const updateSize = () => {
            if (window.innerWidth >= 768 && mdSize) {
                setCurrentSize(mdSize);
            } else {
                setCurrentSize(size);
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [size, mdSize]);

    const radius = (currentSize - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={currentSize} height={currentSize} className="transform -rotate-90">
                <circle
                    className="text-slate-100"
                    strokeWidth={strokeWidth}
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={currentSize / 2}
                    cy={currentSize / 2}
                />
                <circle
                    className={`${color} transition-all duration-500 ease-out`}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={currentSize / 2}
                    cy={currentSize / 2}
                />
            </svg>
            <span className="absolute text-[8px] md:text-[10px] font-black text-slate-700">{Math.round(progress)}%</span>
        </div>
    );
};

const TeacherDirectoryModal = ({ isOpen, onClose, childClasses }: {
    isOpen: boolean;
    onClose: () => void;
    childClasses: Class[];
}) => {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-[2rem] shadow-2xl max-w-lg w-full flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 sm:px-6 sm:py-5 border-b flex justify-between items-center bg-slate-50 shrink-0">
                    <h3 className="text-lg sm:text-xl font-black text-slate-800 flex items-center gap-2">
                        <PhoneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600" /> Danh bạ giáo viên
                    </h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><XMarkIcon className="h-5 w-5 text-gray-600" /></button>
                </div>
                <div className="p-4 sm:p-6 overflow-y-auto space-y-3 sm:space-y-4 flex-1 custom-scrollbar">
                    {childClasses.length === 0 ? (
                        <p className="text-center text-gray-500 py-10 font-medium italic">Chưa có thông tin giáo viên từ các lớp của con.</p>
                    ) : (
                        childClasses.map(cls => (
                            <div key={cls.id} className="p-3 sm:p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">{cls.name}</p>
                                    <p className="font-black text-slate-800 mt-0.5 text-base sm:text-lg truncate">{cls.teacherName || 'Giáo viên phụ trách'}</p>
                                    <p className="text-xs text-slate-500 font-bold flex items-center gap-1 mt-1">
                                        <AcademicCapIcon className="h-3.5 w-3.5" /> Môn học: {cls.gradeLevel}
                                    </p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    {cls.teacherPhone ? (
                                        <>
                                            <a 
                                                href={`tel:${cls.teacherPhone.replace(/\s/g, '')}`} 
                                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 transition-all active:scale-95 text-xs font-black uppercase tracking-tight" 
                                                title="Gọi điện"
                                            >
                                                <PhoneIcon className="h-4 w-4" />
                                                <span>Gọi điện</span>
                                            </a>
                                            <a 
                                                href={`https://zalo.me/${cls.teacherPhone.replace(/\s/g, '')}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-all active:scale-95 text-xs font-black uppercase tracking-tight" 
                                                title="Nhắn tin Zalo"
                                            >
                                                <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                                                <span>Nhắn Zalo</span>
                                            </a>
                                        </>
                                    ) : (
                                        <div className="px-3 py-2 bg-slate-100 text-slate-400 rounded-xl text-[10px] font-bold uppercase tracking-tighter">
                                            Chưa có thông tin liên hệ
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t text-center text-[9px] sm:text-[10px] text-slate-400 font-black uppercase tracking-widest">
                    Hỗ trợ trao đổi trực tiếp 24/7
                </div>
            </div>
        </div>
    );
};

const FindClassModal = ({ isOpen, onClose, onJoin, selectedChild, children = [], onSelectChild, classes, students, joinRequests, user, isSending = false, initialCode = '', initialStudentId = null, limits, navigate, onSaveChild }: {
    isOpen: boolean;
    onClose: () => void;
    onJoin: (foundClass: Class, student?: Student, childId?: string, extraInfo?: { 
        parentName: string; 
        parentPhone: string; 
        relationship: string; 
        childDisplayName: string; 
        updateProfile?: boolean;
        childDob?: string;
        childGender?: 'male' | 'female' | 'other';
    }) => void;
    selectedChild?: Child | null;
    children?: Child[];
    onSelectChild?: (child: Child) => void;
    classes: Class[];
    students: Student[];
    joinRequests: any[];
    user: any;
    isSending?: boolean;
    initialCode?: string;
    initialStudentId?: string | null;
    limits?: { maxChildren: number };
    navigate: (to: string) => void;
    onSaveChild?: (child: Omit<Child, 'id'>) => Promise<Child | null>;
}) => {
    const [code, setCode] = useState('');
    const [step, setStep] = useState<'select_child' | 'code' | 'select_student' | 'confirm_info' | 'create_child'>('code');
    const [foundClass, setTargetClass] = useState<Class | null>(null);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [foundStudents, setFoundStudents] = useState<Student[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [parentName, setParentName] = useState('');
    const [parentPhone, setParentPhone] = useState('');
    const [relationship, setRelationship] = useState('Bố');
    const [childDisplayName, setChildDisplayName] = useState('');
    const [shouldUpdateProfile, setShouldUpdateProfile] = useState(false);
    const [shouldEditParentInfo, setShouldEditParentInfo] = useState(false);
    const [childToSelect, setChildToSelect] = useState<Child | null>(null);
    const [capturedChildForConfirm, setCapturedChildForConfirm] = useState<Child | null>(null);
    const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
    const [wantToAddNewChild, setWantToAddNewChild] = useState(false);
    const [searchStudentQuery, setSearchStudentQuery] = useState('');
    const [notFoundInList, setNotFoundInList] = useState(false);
    
    // States for create_child step
    const [newChildName, setNewChildName] = useState('');
    const [newChildRealName, setNewChildRealName] = useState('');
    const [newChildDob, setNewChildDob] = useState('');
    const [newChildGender, setNewChildGender] = useState<'male' | 'female' | 'other'>('male');
    const [isCreatingChild, setIsCreatingChild] = useState(false);
    
    const resolvedChildRef = useRef<Child | null>(null);
    const codeInputRef = useRef<HTMLInputElement>(null);
    const hasAutoSearchedRef = useRef(false);
    const prevIsOpenRef = useRef(false);

    const normalizeCode = (c: unknown): string => {
        if (c == null) return '';
        if (typeof c === 'string') return c.replace(/\s/g, '').toUpperCase().trim();
        if (typeof c === 'object' && c !== null && 'joinCode' in (c as object)) return normalizeCode((c as { joinCode?: unknown }).joinCode);
        return '';
    };

    // Helper: Tìm con khớp với học sinh dựa trên tên
    const findMatchingChild = (childList: Child[], student?: Student | null) => {
        if (!student || !childList || childList.length === 0) return null;
        const stuName = (student.name || (student as any).realName || '').toLowerCase().trim();
        if (!stuName) return null;
        
        return childList.find(c => {
            const cName = (c.name || (c as any).realName || '').toLowerCase().trim();
            return cName && (stuName.includes(cName) || cName.includes(stuName) || stuName === cName);
        }) || null;
    };

    const handleSearchClass = async (searchCodeOverride?: string) => {
        const codeToUse = normalizeCode(searchCodeOverride ?? code);
        if (!codeToUse) {
            alert('Vui lòng nhập mã lớp (6 ký tự do giáo viên cung cấp).');
            return;
        }
        if (APP_MODE === 'PRODUCTION') {
            setIsSearching(true);
            try {
                const cls = await findClassByCode(codeToUse);
                if (cls) {
                    const mappedClass: Class = {
                        id: String(cls.id),
                        name: cls.name,
                        academicYear: cls.academic_year || '',
                        gradeLevel: cls.grade_level as any,
                        studentIds: [], // We'll fetch students separately
                        classType: cls.class_type as any,
                        enrollmentMode: (cls.enrollment_mode === 'student_code' ? 'manual' : cls.enrollment_mode) as any,
                        allowJoinFromOutside: (cls as any).allow_join_from_outside ?? (cls.enrollment_mode !== 'manual'),
                        location: cls.location,
                        inviteCode: cls.invite_code,
                        teacherName: cls.teacher_name
                    };
                    setTargetClass(mappedClass);
                    
                    // Fetch students for this class using invite code for auth
                    const stuList = await getClassStudents(cls.id, codeToUse);
                    const mapped = stuList.map(s => ({
                        id: String(s.id),
                        name: s.name,
                        realName: (s as any).real_name,
                        studentId: s.student_id_code,
                        dob: s.dob,
                        parents: (s as any).parents
                    }));
                    setFoundStudents(mapped);
                    
                    // Logic xác định con nào sẽ được dùng để đối chiếu
                    const stuForMatching = initialStudentId ? mapped.find(s => String(s.id) === String(initialStudentId)) : null;
                    const matchedChild = findMatchingChild(children, stuForMatching);
                    const childToCapture = selectedChild ?? resolvedChildRef.current ?? matchedChild ?? (children.length === 1 ? children[0] : null);
                    
                    if (childToCapture) setCapturedChildForConfirm(childToCapture);
                    
                    // Pre-select student khi mở từ link theo học sinh
                    if (initialStudentId && mapped.some(s => String(s.id) === String(initialStudentId))) {
                        setSelectedStudentId(String(initialStudentId));
                        // TIỆN LỢI: Nếu đã có studentId mục tiêu, chuyển thẳng sang bước xác nhận
                        setStep('confirm_info');
                    } else {
                        setStep('select_student');
                    }
                } else {
                    alert('Không tìm thấy lớp học với mã này.');
                }
            } catch (e: any) {
                console.error('[FindClass]', e);
                alert(e?.message || 'Lỗi khi tìm kiếm lớp học.');
            } finally {
                setIsSearching(false);
            }
        } else {
            const cls = classes.find(c => (c.inviteCode || '').toUpperCase() === codeToUse);
            if (cls) {
                const stuList = students.filter(s => cls.studentIds.includes(String(s.id)));
                setTargetClass(cls);
                setFoundStudents(stuList);
                
                const stuForMatching = initialStudentId ? stuList.find(s => String(s.id) === String(initialStudentId)) : null;
                const matchedChild = findMatchingChild(children, stuForMatching);
                const childToCapture = selectedChild ?? resolvedChildRef.current ?? matchedChild ?? (children.length === 1 ? children[0] : null);
                
                if (childToCapture) setCapturedChildForConfirm(childToCapture);
                
                if (initialStudentId && stuList.some(s => String(s.id) === String(initialStudentId))) {
                    setSelectedStudentId(String(initialStudentId));
                    setStep('confirm_info');
                } else {
                    setStep('select_student');
                }
            } else {
                alert('Không tìm thấy lớp học với mã này.');
            }
        }
    };

    const handleConfirmAndJoin = () => {
        const matchedChild = findMatchingChild(children, selectedStudent);
        const effectiveChild = wantToAddNewChild ? null : (capturedChildForConfirm ?? selectedChild ?? resolvedChildRef.current ?? matchedChild ?? (children.length === 1 ? children[0] : null));
        const student = notFoundInList ? undefined : foundStudents.find(s => s.id === selectedStudentId);
        const childIdToSend = wantToAddNewChild ? null : (effectiveChild?.id ? String(effectiveChild.id) : undefined);
        if (children.length >= 1 && !effectiveChild && !wantToAddNewChild) {
            alert('Vui lòng chọn con trong danh sách trước khi gửi yêu cầu.');
            return;
        }
        if (!notFoundInList && !childIdToSend && childIdToSend !== null && !student?.id) {
            alert('Vui lòng chọn học sinh trong danh sách lớp trước khi gửi yêu cầu.');
            return;
        }
        if (notFoundInList && !childDisplayName?.trim()) {
            alert('Vui lòng điền tên con theo danh sách lớp khi không tìm thấy trong danh sách.');
            return;
        }
        localStorage.setItem('eduapp_parent_info', JSON.stringify({ parentName, parentPhone, relationship }));
        if (foundClass) onJoin(foundClass, student, childIdToSend, { 
            parentName, 
            parentPhone, 
            relationship, 
            childDisplayName: childDisplayName || newChildName || student?.name || (student as any)?.realName || '',
            updateProfile: isProfileDifferent && shouldUpdateProfile,
            childDob: newChildDob || (student as any)?.dob,
            childGender: newChildGender
        });
    };

    useEffect(() => {
        if (selectedChild) resolvedChildRef.current = selectedChild;
    }, [selectedChild]);

    useEffect(() => {
        if (step === 'code') {
            setSearchStudentQuery('');
            setNotFoundInList(false);
        }
    }, [step]);

    const isProfileDifferent = useMemo(() => {
        return parentName !== (user.teacherName || user.email) || parentPhone !== (user.phone || '');
    }, [parentName, parentPhone, user]);

    useEffect(() => {
        if (!isOpen) {
            setStep('code');
            setTargetClass(null);
            setSelectedStudentId('');
            setFoundStudents([]);
            setIsSearching(false);
            setCode('');
            setChildToSelect(null);
            resolvedChildRef.current = null;
            setCapturedChildForConfirm(null);
            setShouldEditParentInfo(false);
            setWantToAddNewChild(false);
            setSearchStudentQuery('');
            setNotFoundInList(false);
            setNewChildName('');
            setNewChildRealName('');
            setNewChildDob('');
            setNewChildGender('male');
            setIsCreatingChild(false);
            hasAutoSearchedRef.current = false;
            prevIsOpenRef.current = false;
        } else {
            const wasJustOpened = !prevIsOpenRef.current;
            prevIsOpenRef.current = true;

            const codeStr = typeof initialCode === 'string' ? initialCode.replace(/\s/g, '').toUpperCase() : '';
            if (codeStr) setCode(codeStr);
            
            // Chỉ reset step khi vừa mới mở modal lần đầu
            if (wasJustOpened) {
                if (!selectedChild && children.length > 1 && onSelectChild) {
                    setStep('select_child');
                } else {
                    setStep('code');
                }
            }
            
            // Tự động tìm lớp khi mở từ link (code + student) - cho phép cả khi chưa có con (0 children)
            const codeForSearch = typeof initialCode === 'string' ? initialCode.replace(/\s/g, '') : '';
            if (codeForSearch.length >= 6 && !hasAutoSearchedRef.current && (selectedChild || children.length >= 1 || (children.length === 0 && initialStudentId))) {
                hasAutoSearchedRef.current = true;
                handleSearchClass(codeForSearch);
            }
        }
    }, [isOpen, selectedChild, children.length, onSelectChild, initialCode, initialStudentId]);

    const selectedStudent = useMemo(() => foundStudents.find(s => s.id === selectedStudentId), [foundStudents, selectedStudentId]);
    
    const studentParents = useMemo(() => {
        const p = (selectedStudent as any)?.parents;
        return Array.isArray(p) ? p : (typeof p === 'string' ? (() => { try { return JSON.parse(p); } catch { return []; } })() : []);
    }, [selectedStudent]);

    const canAddMoreChildren = useMemo(() => {
        return checkLimit(children.length, limits?.maxChildren ?? EDU_CONFIG.DEFAULT_LIMITS.MAX_CHILDREN);
    }, [children.length, limits]);

    const childMatchStatus = useMemo(() => {
        // Ưu tiên wantToAddNewChild trước để ép hiện form tạo con mới
        if (wantToAddNewChild) {
            return { hasChild: false, matched: false, child: null };
        }
        
        const matchedChild = findMatchingChild(children, selectedStudent);
        const child = capturedChildForConfirm ?? selectedChild ?? resolvedChildRef.current ?? matchedChild ?? (children.length === 1 ? children[0] : null);
        if (child) {
            const stuName = (selectedStudent?.name || (selectedStudent as any)?.realName || '').toLowerCase();
            const childName = (child.name || (child as any).realName || '').toLowerCase();
            const match = stuName && childName && (stuName.includes(childName) || childName.includes(stuName) || stuName === childName);
            return { hasChild: true, matched: !!match, child };
        }
        return { hasChild: false, matched: false, child: null };
    }, [capturedChildForConfirm, selectedChild, resolvedChildRef, children, selectedStudent, wantToAddNewChild]);

    useEffect(() => {
        if (isOpen && step === 'confirm_info') {
            const savedInfo = localStorage.getItem('eduapp_parent_info');
            if (savedInfo) {
                try {
                    const info = JSON.parse(savedInfo);
                    if (!parentName) setParentName(info.parentName || user.teacherName || user.email || '');
                    if (!parentPhone) setParentPhone(info.parentPhone || user.phone || '');
                    if (!relationship) setRelationship(info.relationship || 'Bố');
                } catch (e) { /* ignore */ }
            } else {
                if (!parentName) setParentName(user.teacherName || user.email || '');
                if (!parentPhone) setParentPhone(user.phone || '');
            }
            if (!childDisplayName) {
                setChildDisplayName(selectedStudent?.name || (selectedStudent as any)?.realName || selectedChild?.name || (selectedChild as any)?.realName || '');
            }

            // TỰ ĐỘNG điền thông tin tạo mới con nếu chưa có tài khoản con
            if (children.length === 0 || wantToAddNewChild) {
                if (!newChildName) setNewChildName(selectedStudent?.name || (selectedStudent as any)?.realName || '');
                if (!newChildRealName) setNewChildRealName((selectedStudent as any)?.realName || '');
                if (!newChildDob) setNewChildDob((selectedStudent as any)?.dob || '');
            }
        }
    }, [isOpen, step, user, selectedChild, foundStudents, selectedStudentId, selectedStudent, parentName, parentPhone, relationship, childDisplayName, children.length, wantToAddNewChild, newChildName, newChildRealName, newChildDob]);

    const existingRequest = useMemo(() => {
        if (!foundClass) return null;
        const matchedChild = findMatchingChild(children, selectedStudent);
        const child = capturedChildForConfirm ?? selectedChild ?? resolvedChildRef.current ?? matchedChild ?? (children.length === 1 ? children[0] : null);
        if (child) {
            return (joinRequests || []).find(r => 
                String(r.class_id) === String(foundClass.id) && 
                String(r.child_id) === String(child.id) &&
                r.status === 'pending'
            );
        }
        return (joinRequests || []).find(r => 
            String(r.class_id) === String(foundClass.id) && 
            String(r.user_id) === String(user?.id) &&
            r.status === 'pending'
        );
    }, [foundClass, capturedChildForConfirm, selectedChild, resolvedChildRef, children, joinRequests, user?.id]);

    const existingRejectedRequest = useMemo(() => {
        if (!foundClass || !selectedChild) return null;
        return (joinRequests || []).find(r => 
            String(r.class_id) === String(foundClass.id) && 
            String(r.child_id) === String(selectedChild.id) &&
            r.status === 'rejected'
        );
    }, [foundClass, selectedChild, joinRequests]);

    const filteredStudents = useMemo(() => {
        const q = (searchStudentQuery || '').trim().toLowerCase();
        let list = [...foundStudents];
        if (q) {
            list = list.filter(s => {
                const name = ((s.name || '') + ' ' + ((s as any).realName || '')).toLowerCase();
                const code = (s.studentId || (s as any).student_id_code || '').toLowerCase();
                const parents = ((s as any).parents || []);
                const parentNames = (Array.isArray(parents) ? parents : []).map((p: any) => (p.name || '').toLowerCase()).join(' ');
                return name.includes(q) || code.includes(q) || parentNames.includes(q);
            });
        }
        
        // Sắp xếp: Học sinh đang được chọn đưa lên đầu
        if (selectedStudentId) {
            list.sort((a, b) => (a.id === selectedStudentId ? -1 : (b.id === selectedStudentId ? 1 : 0)));
        }
        
        return list;
    }, [foundStudents, searchStudentQuery, selectedStudentId]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full flex flex-col max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="overflow-y-auto custom-scrollbar flex-1">
                    {step === 'select_child' ? (
                    <div className="p-8">
                        <h3 className="text-xl font-black text-slate-800 mb-2">Chọn con cần tìm lớp</h3>
                        <p className="text-slate-500 text-sm mb-6 font-medium">Bạn có nhiều con. Vui lòng chọn con cần gửi yêu cầu tham gia lớp.</p>
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                            {children.map(child => (
                                <label key={child.id} className={`flex items-center p-4 rounded-2xl border-2 cursor-pointer transition-all ${childToSelect?.id === child.id && !wantToAddNewChild ? 'border-sky-500 bg-sky-50' : 'border-slate-100 hover:border-slate-200'}`}>
                                    <input 
                                        type="radio" 
                                        name="childSelect" 
                                        className="hidden" 
                                        checked={childToSelect?.id === child.id && !wantToAddNewChild}
                                        onChange={() => { setChildToSelect(child); setWantToAddNewChild(false); }}
                                    />
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-sky-600 border border-sky-100 mr-3">
                                        {child.name.charAt(0)}
                                    </div>
                                    <span className="font-bold text-slate-700">{child.name}</span>
                                    {childToSelect?.id === child.id && !wantToAddNewChild && <CheckIcon className="h-5 w-5 text-sky-600 ml-auto" />}
                                </label>
                            ))}
                            {canAddMoreChildren && (
                                <label className={`flex items-center p-4 rounded-2xl border-2 cursor-pointer transition-all ${wantToAddNewChild ? 'border-teal-500 bg-teal-50' : 'border-slate-100 hover:border-slate-200'}`}>
                                    <input 
                                        type="radio" 
                                        name="childSelect" 
                                        className="hidden" 
                                        checked={wantToAddNewChild}
                                        onChange={() => { setWantToAddNewChild(true); setChildToSelect(null); }}
                                    />
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-teal-600 border border-teal-100 mr-3">
                                        +
                                    </div>
                                    <span className="font-bold text-teal-700">Tạo tài khoản con mới</span>
                                    {wantToAddNewChild && <CheckIcon className="h-5 w-5 text-teal-600 ml-auto" />}
                                </label>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <button onClick={onClose} className="py-4 bg-slate-100 text-slate-600 font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all">Bỏ qua</button>
                            <button 
                                disabled={!childToSelect && !wantToAddNewChild}
                                onClick={() => { 
                                    if (wantToAddNewChild) {
                                        if (foundClass) {
                                            // Nếu đã tìm thấy lớp, đi thẳng đến confirm để hiện form tạo con mới
                                            if (onSelectChild) onSelectChild(null as any);
                                            setStep('confirm_info');
                                        } else {
                                            setStep('code');
                                        }
                                    } else if (childToSelect && onSelectChild) { 
                                        onSelectChild(childToSelect); 
                                        if (foundClass) {
                                            setStep('confirm_info');
                                        } else {
                                            setStep('code'); 
                                        }
                                    } 
                                }}
                                className="py-4 bg-sky-600 disabled:bg-slate-200 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-sky-700 transition-all shadow-xl shadow-sky-100 active:scale-95 flex items-center justify-center gap-2"
                            >
                                Tiếp theo
                            </button>
                        </div>
                    </div>
                ) : step === 'code' ? (
                    <div className="p-8 text-center">
                        <div className="w-20 h-20 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <MagnifyingGlassIcon className="h-10 w-10" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 mb-2">Tìm lớp của con</h3>
                        {selectedChild && (
                            <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 bg-sky-50 text-sky-700 rounded-full text-xs font-bold border border-sky-100">
                                Kết nối cho: {selectedChild.name}
                            </div>
                        )}
                        <p className="text-slate-500 text-sm font-medium mb-8">Nhập mã tham gia (6 ký tự) do giáo viên cung cấp.</p>
                        
                            <input 
                                ref={codeInputRef}
                                type="text" 
                                value={code} 
                                onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))} 
                                placeholder="Mã: EDU..." 
                                maxLength={6}
                            className="w-full text-center text-3xl font-black tracking-[0.5em] py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-sky-500 focus:bg-white transition-all outline-none uppercase placeholder:text-slate-200"
                        />
                        
                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <button onClick={onClose} className="py-4 bg-slate-100 text-slate-600 font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all">Bỏ qua</button>
                            <button 
                                disabled={normalizeCode(code).length < 6 || isSearching}
                                onClick={() => {
                                    const raw = codeInputRef.current?.value ?? '';
                                    const codeFromInput = String(raw).replace(/\s/g, '').toUpperCase().trim();
                                    handleSearchClass(codeFromInput || undefined);
                                }} 
                                className="py-4 bg-sky-600 disabled:bg-slate-200 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-sky-700 transition-all shadow-xl shadow-sky-100 active:scale-95 flex items-center justify-center gap-2"
                            >
                                {isSearching && <Spinner className="h-4 w-4" />}
                                <span>Tìm lớp</span>
                            </button>
                        </div>
                    </div>
                ) : step === 'select_student' ? (
                    <div className="p-8">
                        {/* Thông tin lớp để tránh nhầm */}
                        {foundClass && (
                            <div className="mb-4 p-4 bg-sky-50 rounded-2xl border border-sky-100">
                                <p className="text-[10px] font-black text-sky-600 uppercase tracking-widest mb-1">Thông tin lớp – kiểm tra đúng lớp trước khi chọn</p>
                                <p className="text-base font-black text-slate-800">{foundClass.name}</p>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-600 font-medium">
                                    {foundClass.gradeLevel && <span>• Khối: {foundClass.gradeLevel}</span>}
                                    {foundClass.academicYear && <span>• Năm học: {foundClass.academicYear}</span>}
                                    {foundClass.teacherName && <span>• GV: {foundClass.teacherName}</span>}
                                    {foundClass.location && <span>• Địa điểm: {foundClass.location}</span>}
                                </div>
                            </div>
                        )}

                        <h3 className="text-xl font-black text-slate-800 mb-2">Xác nhận học sinh</h3>
                        
                        {/* Cảnh báo Mismatch - Hướng dẫn thông minh khi tên không khớp */}
                        {selectedStudentId && !notFoundInList && !childMatchStatus.matched && !wantToAddNewChild && (
                            <div className="mb-4 p-4 bg-rose-50 border-2 border-rose-100 rounded-2xl animate-in zoom-in-95 duration-200 shadow-sm shadow-rose-100/50">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-rose-100 rounded-xl text-rose-600">
                                        <ExclamationTriangleIcon className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-black text-rose-800 uppercase tracking-tight mb-1">Cẩn thận: Tên không khớp!</p>
                                        <p className="text-[11px] font-medium text-rose-700 leading-normal">
                                            Bạn đang chọn kết nối cho con <span className="font-black underline">&quot;{childMatchStatus.child?.name}&quot;</span> với học sinh <span className="font-black underline">&quot;{selectedStudent?.name}&quot;</span>.
                                        </p>
                                        <div className="mt-3 flex flex-col gap-2">
                                            {canAddMoreChildren ? (
                                                <button 
                                                    type="button" 
                                                    onClick={() => { 
                                                        setNewChildName(selectedStudent?.name || ''); 
                                                        setNewChildRealName((selectedStudent as any)?.realName || '');
                                                        setNewChildDob((selectedStudent as any)?.dob || '');
                                                        setStep('create_child'); 
                                                    }}
                                                    className="inline-flex items-center self-start gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-teal-700 transition-all shadow-md active:scale-95"
                                                >
                                                    <PlusIcon className="h-3.5 w-3.5" />
                                                    Tạo tài khoản mới cho con thứ {children.length + 1}
                                                </button>
                                            ) : (
                                                <button 
                                                    type="button" 
                                                    onClick={() => { onClose(); navigate('/pricing'); }}
                                                    className="w-full py-3 bg-amber-600 border-2 border-amber-500 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-100 active:scale-95"
                                                >
                                                    <AcademicCapIcon className="h-5 w-5" />
                                                    Nâng cấp để thêm con &quot;{selectedStudent?.name}&quot;
                                                </button>
                                            )}
                                            <p className="text-[10px] text-rose-600 font-bold italic text-center mt-1">
                                                * Nếu bạn chỉ có 1 con tên là &quot;{childMatchStatus.child?.name}&quot;, hãy kiểm tra lại có chọn nhầm tên học sinh khác không.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <p className="text-slate-500 text-sm mb-4 font-medium">
                            Bạn đang đăng ký tài khoản của con <span className="text-sky-600 font-bold">{wantToAddNewChild ? 'mới' : (selectedChild?.name || childToSelect?.name || '...')}</span> tham gia lớp <span className="text-sky-600 font-bold">{foundClass?.name}</span>.
                            {foundStudents.length > 0 && ' Tìm và chọn con của bạn trong danh sách:'}
                        </p>

                        {foundStudents.length > 0 && (
                            <div className="mb-4">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <input
                                        type="text"
                                        value={searchStudentQuery}
                                        onChange={e => setSearchStudentQuery(e.target.value)}
                                        placeholder="Tìm theo tên, mã HS, tên phụ huynh..."
                                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border-2 border-slate-100 rounded-xl text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:border-sky-500 focus:bg-white outline-none"
                                    />
                                    {searchStudentQuery && (
                                        <button type="button" onClick={() => setSearchStudentQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                                {searchStudentQuery && (
                                    <p className="text-[10px] text-slate-500 mt-1 font-medium">
                                        Tìm thấy {filteredStudents.length} / {foundStudents.length} học sinh
                                    </p>
                                )}
                            </div>
                        )}

                        {existingRequest && (
                            <div className="p-4 mb-4 bg-amber-50 border border-amber-100 rounded-2xl">
                                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest leading-tight">
                                    Bạn đã gửi 1 yêu cầu tham gia lớp này cho con và đang chờ giáo viên phê duyệt. Không thể gửi thêm yêu cầu mới.
                                </p>
                            </div>
                        )}
                        {existingRejectedRequest && (
                            <div className="p-4 mb-4 bg-rose-50 border border-rose-100 rounded-2xl">
                                <div className="flex items-center gap-2 mb-1">
                                    <XMarkIcon className="h-3 w-3 text-rose-600" />
                                    <p className="text-[10px] font-bold text-rose-700 uppercase tracking-widest">Yêu cầu trước đó bị từ chối</p>
                                </div>
                                {((existingRejectedRequest as any).reject_reason || (existingRejectedRequest as any).rejectReason) && (
                                    <p className="text-sm text-rose-700 font-medium mb-2 italic">Lý do: {(existingRejectedRequest as any).reject_reason || (existingRejectedRequest as any).rejectReason}</p>
                                )}
                                <p className="text-[10px] text-rose-600 font-medium">Bạn có thể điều chỉnh thông tin và gửi lại yêu cầu mới.</p>
                            </div>
                        )}
                        
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                            {foundStudents.length > 0 ? (
                                <>
                                    {filteredStudents.map(student => (
                                        <label key={student.id} className={`flex items-center p-4 rounded-2xl border-2 cursor-pointer transition-all ${selectedStudentId === student.id && !notFoundInList ? 'border-sky-500 bg-sky-50' : 'border-slate-100 hover:border-slate-200'}`}>
                                            <input 
                                                type="radio" 
                                                name="studentSelect" 
                                                className="hidden" 
                                                value={student.id} 
                                                checked={selectedStudentId === student.id && !notFoundInList}
                                                onChange={() => { setSelectedStudentId(String(student.id)); setNotFoundInList(false); }}
                                            />
                                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-sky-600 border border-sky-100 mr-3 shrink-0">
                                                {student.name.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="font-bold text-slate-700">{student.name}</span>
                                                {(student as any).realName && <span className="ml-1 text-xs text-slate-500 italic">({(student as any).realName})</span>}
                                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[10px] text-slate-500 font-medium">
                                                    {(student.studentId || (student as any).student_id_code) && <span>Mã: {student.studentId || (student as any).student_id_code}</span>}
                                                    {(student.dob || (student as any).dob) && <span>• Sinh: {new Date(student.dob || (student as any).dob).toLocaleDateString('vi-VN')}</span>}
                                                    {(() => {
                                                        const p = (student as any).parents;
                                                        const arr = Array.isArray(p) ? p : (typeof p === 'string' ? (() => { try { return JSON.parse(p); } catch { return []; } })() : []);
                                                        return arr.length > 0 ? <span>• PH: {arr.map((x: any) => x.name).filter(Boolean).join(', ')}</span> : null;
                                                    })()}
                                                </div>
                                            </div>
                                            {selectedStudentId === student.id && !notFoundInList && <CheckIcon className="h-5 w-5 text-sky-600 ml-auto shrink-0" />}
                                        </label>
                                    ))}
                                    {filteredStudents.length === 0 && searchStudentQuery && (
                                        <div className="py-6 px-4 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                            <p className="text-sm text-slate-600 font-medium">Không tìm thấy kết quả với &quot;{searchStudentQuery}&quot;</p>
                                            <p className="text-xs text-slate-500 mt-1">Thử từ khóa khác hoặc chọn &quot;Không tìm thấy tên con&quot; bên dưới</p>
                                        </div>
                                    )}
                                    {/* Lựa chọn khi không tìm thấy tên */}
                                    <label className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all mt-2 ${notFoundInList ? 'border-amber-500 bg-amber-50' : 'border-amber-100 hover:border-amber-200 bg-amber-50/50'}`}>
                                        <input 
                                            type="radio" 
                                            name="studentSelect" 
                                            className="hidden" 
                                            value="not_found" 
                                            checked={notFoundInList}
                                            onChange={() => { setNotFoundInList(true); setSelectedStudentId(''); }}
                                        />
                                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                                        <div>
                                            <span className="font-bold text-amber-800">Tôi không tìm thấy tên con trong danh sách</span>
                                            <p className="text-[10px] text-amber-700 mt-1 font-medium">
                                                Nếu con chưa có trong danh sách, vui lòng liên hệ giáo viên để được thêm vào lớp trước. Không chọn nhầm tên học sinh khác.
                                            </p>
                                        </div>
                                        {notFoundInList && <CheckIcon className="h-5 w-5 text-amber-600 ml-auto shrink-0" />}
                                    </label>
                                </>
                            ) : (
                                <div className="py-8 px-4 text-center bg-amber-50 rounded-2xl border border-dashed border-amber-200">
                                    <p className="text-sm text-amber-700 font-bold">Lớp chưa có danh sách học sinh</p>
                                    <p className="text-xs text-amber-600 mt-1">Bạn vẫn có thể gửi yêu cầu tham gia. Giáo viên sẽ kiểm tra và thêm con vào lớp sau.</p>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <button onClick={() => setStep('code')} className="py-4 bg-slate-100 text-slate-600 font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all">Quay lại</button>
                            <button 
                                disabled={(foundStudents.length > 0 && !selectedStudentId && !notFoundInList) || !!existingRequest || isCheckingEligibility || (children.length >= 1 && !(selectedChild ?? resolvedChildRef.current ?? findMatchingChild(children, selectedStudent) ?? (children.length === 1 ? children[0] : null)))}
                                onClick={async () => {
                                    const childToUse = selectedChild ?? resolvedChildRef.current ?? findMatchingChild(children, selectedStudent) ?? (children.length === 1 ? children[0] : null);
                                    if (!foundClass) return;

                                    // Chặn lại nếu tên không khớp để nhắc nhở phụ huynh
                                    if (selectedStudentId && !notFoundInList && !childMatchStatus.matched && !wantToAddNewChild) {
                                        const confirmText = `CẢNH BÁO: Tên con "${childMatchStatus.child?.name}" KHÔNG KHỚP với học sinh "${selectedStudent?.name}" bạn chọn.\n\n- Nếu bạn muốn thêm con mới vào lớp này, hãy chọn "Tạo tài khoản mới" ở phía trên.\n- Nếu bạn chắc chắn đây là đúng con mình (ví dụ GV nhập sai tên), bấm OK để tiếp tục.`;
                                        if (!window.confirm(confirmText)) return;
                                    }

                                    if (childToUse && APP_MODE === 'PRODUCTION' && !notFoundInList) {
                                        setIsCheckingEligibility(true);
                                        try {
                                            const { canJoin, message } = await checkJoinEligibility(foundClass.id, childToUse.id, selectedStudentId || undefined);
                                            if (!canJoin) {
                                                alert(message || 'Không thể gửi yêu cầu. Vui lòng kiểm tra lại.');
                                                return;
                                            }
                                        } finally {
                                            setIsCheckingEligibility(false);
                                        }
                                    }
                                    setCapturedChildForConfirm(childToUse ?? null);
                                    setStep('confirm_info');
                                }} 
                                className={`py-4 font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-xl active:scale-95 flex items-center justify-center gap-2 ${
                                    selectedStudentId && !notFoundInList && !childMatchStatus.matched && !wantToAddNewChild 
                                        ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-100 text-white' 
                                        : 'bg-teal-600 hover:bg-teal-700 shadow-teal-100 text-white'
                                } disabled:bg-slate-200 disabled:shadow-none`}
                            >
                                {isCheckingEligibility && <Spinner className="h-4 w-4" />}
                                {existingRequest ? 'Đã gửi yêu cầu' : (selectedStudentId && !notFoundInList && !childMatchStatus.matched && !wantToAddNewChild ? 'Vẫn tiếp tục (Tên không khớp)' : 'Tiếp theo')}
                            </button>
                        </div>
                    </div>
                ) : step === 'create_child' ? (
                    <div className="p-8 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-2xl flex items-center justify-center font-black">
                                <PlusIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-800 leading-none">Tạo hồ sơ con mới</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter mt-1">Thông tin con thứ {children.length + 1} của bạn</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tên đầy đủ <span className="text-rose-500">*</span></label>
                                <input 
                                    type="text" 
                                    value={newChildName} 
                                    onChange={e => setNewChildName(e.target.value)} 
                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700 placeholder:text-slate-300" 
                                    placeholder="Nguyễn Văn An" 
                                />
                                <p className="mt-1 text-[9px] text-teal-600 italic font-medium tracking-tight uppercase">Dùng để giáo viên nhận diện con khi tham gia lớp học.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Biệt danh / Tên gọi</label>
                                <input 
                                    type="text" 
                                    value={newChildRealName} 
                                    onChange={e => setNewChildRealName(e.target.value)} 
                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700 placeholder:text-slate-300" 
                                    placeholder="Ví dụ: Miu, An (tùy chọn)" 
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ngày sinh <span className="text-rose-500">*</span></label>
                                    <input 
                                        type="date" 
                                        value={newChildDob} 
                                        onChange={e => setNewChildDob(e.target.value)} 
                                        className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Giới tính</label>
                                    <select 
                                        value={newChildGender} 
                                        onChange={e => setNewChildGender(e.target.value as any)} 
                                        className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700"
                                    >
                                        <option value="male">Nam</option>
                                        <option value="female">Nữ</option>
                                        <option value="other">Khác</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <button onClick={() => setStep('select_student')} className="py-4 bg-slate-100 text-slate-600 font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all">Quay lại</button>
                            <button 
                                disabled={!newChildName.trim() || !newChildDob || isCreatingChild}
                                onClick={async () => {
                                    if (!onSaveChild) return;
                                    setIsCreatingChild(true);
                                    try {
                                        const res = await onSaveChild({
                                            name: newChildName,
                                            realName: newChildRealName,
                                            dob: newChildDob,
                                            gender: newChildGender,
                                            studentId: selectedStudentId && !notFoundInList ? selectedStudentId : ''
                                        });
                                        // Wait a tiny bit for the state to propagate in Parent component
                                        // Actually the handleSave in App.tsx returns the created item
                                        if (res) {
                                            if (onSelectChild) onSelectChild(res as any);
                                            setCapturedChildForConfirm(res as any);
                                            setWantToAddNewChild(false);
                                            setStep('confirm_info');
                                        }
                                    } catch (e) {
                                        console.error('[CreateChild]', e);
                                        alert('Lỗi khi tạo hồ sơ con. Vui lòng thử lại.');
                                    } finally {
                                        setIsCreatingChild(false);
                                    }
                                }} 
                                className="py-4 bg-teal-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-teal-700 transition-all shadow-xl shadow-teal-100 active:scale-95 flex items-center justify-center gap-2"
                            >
                                {isCreatingChild ? <Spinner className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}
                                <span>Lưu hồ sơ & Tiếp theo</span>
                            </button>
                        </div>
                    </div>
                ) : step === 'confirm_info' ? (
                    <div className="p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-sky-100 text-sky-600 rounded-2xl flex items-center justify-center font-black">
                                <InformationCircleIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-800 leading-none">Đối chiếu thông tin</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter mt-1">Thông tin từ hồ sơ giáo viên – kiểm tra và điều chỉnh nếu cần</p>
                            </div>
                        </div>

                        {notFoundInList && (
                            <div className="p-4 mb-4 bg-amber-50 border border-amber-200 rounded-2xl">
                                <div className="flex items-start gap-2 mb-3">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-bold text-amber-800">Con chưa có trong danh sách lớp</p>
                                        <p className="text-[10px] text-amber-700 mt-0.5">Bạn đã chọn &quot;Không tìm thấy tên con&quot;. Vui lòng điền tên con theo danh sách lớp (hoặc tên đã đăng ký với giáo viên) để giáo viên xác nhận.</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tên con theo danh sách lớp <span className="text-rose-500">*</span></label>
                                    <input type="text" value={childDisplayName} onChange={e => setChildDisplayName(e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-amber-200 rounded-xl focus:border-amber-500 outline-none font-bold text-slate-700" placeholder="Họ và tên đầy đủ" />
                                </div>
                            </div>
                        )}

                        {/* Thông tin từ hồ sơ GV */}
                        <div className="p-4 mb-4 bg-amber-50/50 rounded-2xl border border-amber-100 space-y-3 shadow-sm">
                            <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest flex items-center gap-1.5">
                                <AcademicCapIcon className="h-4 w-4" /> Thông tin từ hồ sơ giáo viên
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {foundClass && (
                                    <div>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Lớp học</p>
                                        <p className="text-sm font-black text-slate-800 leading-tight">{foundClass.name}</p>
                                        {foundClass.teacherName && <p className="text-[10px] text-slate-500 font-medium">GV: {foundClass.teacherName}</p>}
                                    </div>
                                )}
                                {selectedStudent && !notFoundInList && (
                                    <div className="space-y-1">
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Học sinh trong lớp</p>
                                        <p className="text-sm font-black text-slate-800 leading-tight">{selectedStudent.name}</p>
                                        {(selectedStudent.dob || (selectedStudent as any).dob) && <p className="text-[10px] text-slate-500 font-medium tracking-tight">Sinh: {new Date(selectedStudent.dob || (selectedStudent as any).dob).toLocaleDateString('vi-VN')}</p>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Thông tin con của bạn (đã chọn hoặc vừa tạo) */}
                        <div className={`p-4 mb-4 rounded-2xl border-2 shadow-sm transition-all ${
                            childMatchStatus.hasChild 
                                ? (childMatchStatus.matched ? 'bg-teal-50/50 border-teal-100' : 'bg-rose-50/50 border-rose-100')
                                : 'bg-amber-50/50 border-amber-100'
                        }`}>
                            <p className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 mb-3 ${
                                childMatchStatus.hasChild 
                                    ? (childMatchStatus.matched ? 'text-teal-700' : 'text-rose-700')
                                    : 'text-amber-700'
                            }`}>
                                <UserCircleIcon className="h-4 w-4" /> Tài khoản con bạn kết nối
                            </p>
                            
                            {childMatchStatus.hasChild ? (
                                <>
                                    <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-white/50 shadow-sm">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border shrink-0 ${
                                            childMatchStatus.matched ? 'bg-teal-50 text-teal-600 border-teal-100' : 'bg-rose-50 text-rose-600 border-rose-100'
                                        }`}>
                                            {childMatchStatus.child?.name?.charAt(0) || 'C'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-black text-slate-800 truncate">{childMatchStatus.child?.name}</p>
                                            {childMatchStatus.child?.dob && (
                                                <p className="text-[10px] text-slate-500 font-bold tracking-tight">
                                                    Sinh: {new Date(childMatchStatus.child.dob).toLocaleDateString('vi-VN')}
                                                </p>
                                            )}
                                        </div>
                                        {childMatchStatus.matched ? (
                                            <div className="flex flex-col items-end">
                                                <CheckBadgeIcon className="h-5 w-5 text-teal-600" />
                                                <span className="text-[9px] font-black text-teal-600 uppercase mt-0.5">Khớp 100%</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-end">
                                                <ExclamationTriangleIcon className="h-5 w-5 text-rose-600" />
                                                <span className="text-[9px] font-black text-rose-600 uppercase mt-0.5">Tên khác nhau</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {!childMatchStatus.matched && (
                                        <div className="mt-3 p-2 bg-rose-100/50 rounded-lg text-[10px] font-medium text-rose-800 leading-relaxed border border-rose-100">
                                            <span className="font-black uppercase">Chú ý:</span> Bạn đang kết nối tài khoản con có tên khác với hồ sơ giáo viên. Vui lòng kiểm tra kỹ để tránh kết nối nhầm.
                                        </div>
                                    )}

                                    <div className="mt-3 flex gap-4 border-t border-slate-100 pt-3">
                                        <button type="button" onClick={() => setStep('select_student')} className="text-[10px] text-sky-600 font-black uppercase hover:underline flex items-center gap-1">
                                            <ArrowPathIcon className="h-3 w-3" /> Chọn học sinh khác
                                        </button>
                                        <button type="button" onClick={() => setStep('select_child')} className="text-[10px] text-sky-600 font-black uppercase hover:underline flex items-center gap-1">
                                            <UserGroupIcon className="h-3 w-3" /> Chọn con khác
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-start gap-2 bg-amber-50 p-4 rounded-xl border border-amber-100 shadow-sm text-amber-800">
                                        <InformationCircleIcon className="h-6 w-6 shrink-0 text-amber-500" />
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-widest mb-1">Tạo hồ sơ con mới</p>
                                            <p className="text-[10px] font-medium leading-relaxed opacity-90">
                                                Hệ thống sẽ tạo ngay hồ sơ con dựa trên thông tin dưới đây để bạn có thể sử dụng các tính năng khác trong khi chờ giáo viên duyệt vào lớp:
                                            </p>
                                        </div>
                                    </div>

                                    {/* Editable New Child Info */}
                                    <div className="p-4 bg-white rounded-2xl border-2 border-amber-100 space-y-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tên của con <span className="text-rose-500">*</span></label>
                                            <input 
                                                type="text" 
                                                value={newChildName} 
                                                onChange={e => setNewChildName(e.target.value)} 
                                                className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700 text-sm" 
                                                placeholder="Tên đầy đủ" 
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ngày sinh <span className="text-rose-500">*</span></label>
                                                <input 
                                                    type="date" 
                                                    value={newChildDob} 
                                                    onChange={e => setNewChildDob(e.target.value)} 
                                                    className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700 text-sm" 
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Giới tính</label>
                                                <select 
                                                    value={newChildGender} 
                                                    onChange={e => setNewChildGender(e.target.value as any)} 
                                                    className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 outline-none font-bold text-slate-700 text-sm"
                                                >
                                                    <option value="male">Nam</option>
                                                    <option value="female">Nữ</option>
                                                    <option value="other">Khác</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {!canAddMoreChildren && (
                                        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl">
                                            <p className="text-[10px] font-bold text-rose-700 leading-tight italic">
                                                * Bạn đã đạt giới hạn tài khoản con. Không thể tạo thêm tài khoản mới lúc này.
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex gap-4 border-t border-slate-100 pt-3">
                                        <button type="button" onClick={() => setStep('select_student')} className="text-[10px] text-sky-600 font-black uppercase hover:underline flex items-center gap-1">
                                            <ArrowPathIcon className="h-3 w-3" /> Chọn học sinh khác
                                        </button>
                                        {children.length > 0 && (
                                            <button type="button" onClick={() => setStep('select_child')} className="text-[10px] text-sky-600 font-black uppercase hover:underline flex items-center gap-1">
                                                <UserGroupIcon className="h-3 w-3" /> Chọn con đã có
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Checkbox điều chỉnh thông tin phụ huynh */}
                        <label className="flex items-start gap-3 p-3 mb-4 bg-slate-50 rounded-2xl border border-slate-100 cursor-pointer">
                            <input type="checkbox" checked={shouldEditParentInfo} onChange={e => setShouldEditParentInfo(e.target.checked)} className="mt-1 h-4 w-4 text-teal-600 rounded" />
                            <span className="text-xs font-bold text-slate-700">Điều chỉnh thông tin phụ huynh (nếu giáo viên nhập sai hoặc cần thêm)</span>
                        </label>

                        {shouldEditParentInfo && (
                        <div className="space-y-4 mb-4 p-4 bg-white rounded-2xl border border-slate-200">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tên phụ huynh</label>
                                <input type="text" value={parentName} onChange={e => setParentName(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-sky-500 outline-none font-bold text-slate-700" placeholder="Họ và tên" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Số điện thoại</label>
                                    <input type="tel" value={parentPhone} onChange={e => setParentPhone(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-sky-500 outline-none font-bold text-slate-700" placeholder="09..." />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Quan hệ</label>
                                        <select value={relationship} onChange={e => setRelationship(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-sky-500 outline-none font-bold text-slate-700">
                                            <option value="Bố">Bố</option>
                                            <option value="Mẹ">Mẹ</option>
                                            <option value="Ông/Bà">Ông/Bà</option>
                                            <option value="Người giám hộ">Người giám hộ</option>
                                        </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tên thật của con (theo danh sách lớp)</label>
                                <input type="text" value={childDisplayName} onChange={e => setChildDisplayName(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-sky-500 outline-none font-bold text-slate-700" placeholder="Tên đầy đủ" />
                            </div>
                        </div>
                        )}

                        {!shouldEditParentInfo && (
                            <div className="mb-4 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Thông tin bạn gửi</p>
                                {studentParents.length > 0 && (
                                    <div className="mb-2">
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1">Quan hệ của bạn với con</label>
                                        <select value={relationship} onChange={e => setRelationship(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700">
                                            <option value="Bố">Bố</option>
                                            <option value="Mẹ">Mẹ</option>
                                            <option value="Ông/Bà">Ông/Bà</option>
                                            <option value="Người giám hộ">Người giám hộ</option>
                                        </select>
                                        <p className="text-[10px] text-slate-500 mt-0.5">Đã có {studentParents.map((p: any) => p.relationship || 'PH').join(', ')} trong hồ sơ. Chọn quan hệ của bạn để bổ sung.</p>
                                    </div>
                                )}
                                <p className="text-sm font-bold text-slate-800">{parentName || user.teacherName || user.email || 'Chưa điền'}</p>
                                <p className="text-xs text-slate-500">{parentPhone || user.phone || ''} • {relationship}</p>
                                <p className="text-xs text-slate-500 mt-1">Con: {childDisplayName || selectedStudent?.name}</p>
                            </div>
                        )}

                            {existingRequest && (
                                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl mb-4">
                                    <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest leading-tight">
                                        Bạn đã gửi 1 yêu cầu tham gia lớp này cho con và đang chờ giáo viên phê duyệt. Không thể gửi thêm yêu cầu mới.
                                    </p>
                                </div>
                            )}

                            {existingRejectedRequest && (
                                <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl mb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <XMarkIcon className="h-3 w-3 text-rose-600" />
                                        <p className="text-[10px] font-bold text-rose-700 uppercase tracking-widest">Yêu cầu trước đó bị từ chối</p>
                                    </div>
                                    {((existingRejectedRequest as any).reject_reason || (existingRejectedRequest as any).rejectReason) && (
                                        <p className="text-sm text-rose-700 font-medium mb-2 italic">Lý do: {(existingRejectedRequest as any).reject_reason || (existingRejectedRequest as any).rejectReason}</p>
                                    )}
                                    <p className="text-[10px] text-rose-600 font-medium">Bạn có thể điều chỉnh thông tin bên dưới và gửi lại yêu cầu mới.</p>
                                </div>
                            )}

                            {isProfileDifferent && (
                                <label className="flex items-start gap-3 p-4 bg-teal-50 rounded-2xl border border-teal-100 cursor-pointer group">
                                    <input 
                                        type="checkbox" 
                                        checked={shouldUpdateProfile} 
                                        onChange={e => setShouldUpdateProfile(e.target.checked)}
                                        className="mt-1 h-4 w-4 text-teal-600 rounded border-teal-300 focus:ring-teal-500"
                                    />
                                    <div>
                                        <p className="text-xs font-bold text-teal-800">Cập nhật thông tin vào hồ sơ của tôi</p>
                                        <p className="text-[10px] text-teal-600 font-medium mt-0.5">Hệ thống nhận thấy thông tin bạn nhập khác với hồ sơ hiện tại. Bạn có muốn lưu lại cho các lần sau không?</p>
                                    </div>
                                </label>
                            )}

                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <button onClick={() => setStep(foundStudents.length > 0 ? 'select_student' : 'code')} className="py-4 bg-slate-100 text-slate-600 font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-slate-200 transition-all">Quay lại</button>
                            <button 
                                disabled={
                                    !(parentName || user?.teacherName || user?.email) || 
                                    !(childDisplayName || newChildName || selectedStudent?.name || (selectedStudent as any)?.realName) || 
                                    !!existingRequest || 
                                    isSending || 
                                    (
                                        // Nếu có con thì phải chọn 1 con, trừ khi chọn "Tạo mới"
                                        children.length >= 1 && !wantToAddNewChild && !childMatchStatus.hasChild
                                    ) ||
                                    (
                                        // Nếu tạo mới thì phải điền đủ tên và ngày sinh
                                        (children.length === 0 || wantToAddNewChild) && (!newChildName?.trim() || !newChildDob)
                                    )
                                }
                                onClick={handleConfirmAndJoin} 
                                className="py-4 bg-teal-600 disabled:bg-slate-200 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-teal-700 shadow-xl shadow-teal-100 active:scale-95 transition-all flex items-center justify-center gap-2 min-h-[52px]"
                            >
                                {isSending && <Spinner className="h-5 w-5 shrink-0" />}
                                <span>{existingRequest ? 'Đã gửi yêu cầu' : isSending ? 'Đang gửi...' : 'Gửi yêu cầu'}</span>
                            </button>
                        </div>
                    </div>
                ) : null}
                </div>
            </div>
        </div>
    );
};

const ChildAccountModal = ({ child, onClose, onSave }: {
    child: Partial<Child> | null;
    onClose: () => void;
    onSave: (child: Child) => void;
}) => {
    const [name, setName] = useState(child?.name || '');
    const [realName, setRealName] = useState(child?.realName || '');
    const [dob, setDob] = useState(child?.dob || '');
    const [gender, setGender] = useState(child?.gender || 'other');
    const [studentId, setStudentId] = useState(child?.studentId || '');
    const [avatarUrl, setAvatarUrl] = useState(child?.avatarUrl || (child as any)?.avatar || '');
    const [isUploading, setIsCreatingAvatar] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSave = () => {
        if (name.trim() && dob) {
            onSave({
                ...child,
                id: child?.id || `temp_${Date.now()}`,
                name: name.trim(),
                realName: realName.trim() || undefined,
                dob,
                gender: gender as 'male' | 'female' | 'other',
                studentId: studentId.trim(),
                avatarUrl,
                avatar: avatarUrl
            } as Child);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 2 * 1024 * 1024) {
                alert("Ảnh đại diện không được vượt quá 2MB");
                return;
            }
            
            setIsCreatingAvatar(true);
            try {
                const uploadedUrl = await uploadImageResource(file);
                setAvatarUrl(uploadedUrl);
            } catch (error) {
                console.error("Lỗi tải ảnh đại diện:", error);
                alert("Không thể tải ảnh lên. Vui lòng thử lại.");
            } finally {
                setIsCreatingAvatar(false);
            }
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full flex flex-col max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center bg-slate-50 shrink-0">
                    <h3 className="text-lg font-bold text-slate-800">{child?.id ? 'Chỉnh sửa thông tin' : 'Thêm tài khoản cho con'}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 transition-colors"><XMarkIcon className="h-5 w-5 text-gray-600" /></button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
                    {/* Avatar Upload Section */}
                    <div className="flex flex-col items-center gap-3">
                        <div className="relative group">
                            <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
                                {avatarUrl ? (
                                    <img src={formatAssetUrl(avatarUrl)} alt="Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="text-4xl">
                                        {gender === 'male' ? '👦' : gender === 'female' ? '👧' : '🧒'}
                                    </div>
                                )}
                                {isUploading && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute bottom-0 right-0 p-2 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 transition-all active:scale-90"
                                title="Tải ảnh lên"
                            >
                                <UploadIcon className="h-4 w-4" />
                            </button>
                        </div>
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            className="hidden" 
                            accept="image/*" 
                            onChange={handleFileChange} 
                        />
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Ảnh đại diện của con</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Tên đầy đủ <span className="text-rose-500">*</span></label>
                            <input type="text" value={name} onChange={e => setName(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500" placeholder="Nguyễn Văn An" />
                            <p className="mt-1 text-[10px] text-teal-600 italic">Dùng để giáo viên nhận diện con khi tham gia lớp học.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Biệt danh / Tên gọi</label>
                            <input type="text" value={realName} onChange={e => setRealName(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500" placeholder="Ví dụ: Miu, An (tùy chọn)" />
                            <p className="mt-1 text-[10px] text-slate-400 italic">Tùy chọn, hiển thị kèm khi có</p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Mã học sinh (Chỉ giáo viên cấp)</label>
                        <input 
                            type="text" 
                            value={studentId} 
                            readOnly 
                            className="mt-1 block w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md shadow-sm text-gray-500 cursor-not-allowed" 
                            placeholder="Chưa có mã" 
                        />
                        <p className="mt-1 text-[10px] text-teal-600 italic font-medium">Mã này được giáo viên cung cấp khi con tham gia lớp học chính thức.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Ngày sinh</label>
                            <input type="date" value={dob} onChange={e => setDob(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Giới tính</label>
                            <select value={gender} onChange={e => setGender(e.target.value as 'male' | 'female' | 'other')} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm rounded-md">
                                <option value="male">Nam</option>
                                <option value="female">Nữ</option>
                                <option value="other">Khác</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-3 flex justify-end rounded-b-lg">
                    <button onClick={handleSave} className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:bg-gray-400" disabled={!name.trim() || !dob || isUploading}>Lưu</button>
                </div>
            </div>
        </div>
    );
};


interface ParentDashboardProps {
    onNavigate: (view: View, payload?: object) => void;
    user: UserSettings;
    customGreeting?: string | null;
    children: Child[];
    classes: Class[];
    students: Student[];
    homeworks: Homework[];
    assignments: Assignment[];
    submissions: Submission[];
    studentStats?: any[];
    notifications?: Notification[];
    joinRequests?: any[];
    onSaveChild: (child: Omit<Child, 'id'>) => Promise<Child | null>;
    onUpdateChild: (child: Child) => void;
    onDeleteChild: (id: string | number) => void;
    onUpdateSettings?: (update: any) => void;
    onRefreshData?: () => void;
    isLoading?: boolean;
}

const NewChildWelcomeModal = ({ isOpen, onClose, childName, onNavigate, targetClassName }: {
    isOpen: boolean;
    onClose: () => void;
    childName: string;
    onNavigate: (view: View, payload?: object) => void;
    targetClassName?: string;
}) => {
    if (!isOpen) return null;

    const suggestions = [
        {
            title: 'Lộ trình học tập AI',
            desc: 'Tạo kế hoạch học tập cá nhân hóa dựa trên thế mạnh của con.',
            icon: <SparklesIcon className="h-6 w-6 text-amber-500" />,
            action: () => onNavigate(View.ChildDashboard, { focusSection: 'plans' }),
            color: 'bg-amber-50'
        },
        {
            title: 'Tạo bài ôn đầu tiên cho con',
            desc: 'Thử soạn hoặc chọn một bài ôn nhỏ trong thư viện để làm quen với hệ thống.',
            icon: <BookOpenIcon className="h-6 w-6 text-teal-500" />,
            action: () => onNavigate(View.DocumentLibrary, {}),
            color: 'bg-teal-50'
        },
        {
            title: 'Phòng Lab Tiếng Anh',
            desc: 'Khám phá các hoạt động luyện phát âm và học từ vựng sinh động.',
            icon: <MegaphoneIcon className="h-6 w-6 text-sky-500" />,
            action: () => onNavigate(View.EnglishLab, {}),
            color: 'bg-sky-50'
        }
    ];

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                <div className="relative p-8 text-center bg-gradient-to-br from-teal-600 to-cyan-700 text-white shrink-0">
                    <div className="absolute top-4 right-4">
                        <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><XMarkIcon className="h-5 w-5" /></button>
                    </div>
                    <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-md border border-white/30">
                        <CheckBadgeIcon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-black mb-2 leading-tight">Chào mừng {childName}!</h3>
                    <p className="text-teal-50/80 text-sm font-medium">Hồ sơ của con đã sẵn sàng. Hãy bắt đầu hành trình khám phá tri thức cùng AI ngay hôm nay.</p>
                </div>
                
                <div className="overflow-y-auto custom-scrollbar flex-1">
                    <div className="p-8 space-y-4">
                        {targetClassName && (
                            <div className="p-4 bg-teal-50 border-2 border-teal-100 rounded-2xl flex items-start gap-3 animate-in slide-in-from-left-2 duration-500">
                                <div className="p-2 bg-teal-100 rounded-xl text-teal-600 shrink-0">
                                    <MegaphoneIcon className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="text-xs font-black text-teal-800 uppercase tracking-tight mb-1">Đã gửi yêu cầu tham gia lớp</p>
                                    <p className="text-[11px] font-medium text-teal-700 leading-normal">
                                        Hệ thống đã gửi yêu cầu kết nối <span className="font-bold">&quot;{childName}&quot;</span> đến lớp <span className="font-bold">&quot;{targetClassName}&quot;</span>. Vui lòng chờ giáo viên phê duyệt để bắt đầu học tập cùng lớp.
                                    </p>
                                </div>
                            </div>
                        )}

                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Gợi ý hoạt động đầu tiên</p>
                        {suggestions.map((s, i) => (
                            <button 
                                key={i} 
                                onClick={() => { onClose(); s.action(); }}
                                className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-slate-50 hover:border-teal-100 hover:bg-teal-50/30 transition-all text-left group"
                            >
                                <div className={`w-12 h-12 ${s.color} rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                                    {s.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-black text-slate-800 mb-0.5">{s.title}</h4>
                                    <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                                </div>
                                <ChevronRightIcon className="h-5 w-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-8 pt-0 shrink-0">
                    <button 
                        onClick={onClose}
                        className="w-full py-4 bg-slate-900 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-slate-800 transition-all shadow-xl active:scale-95"
                    >
                        Khám phá Dashboard
                    </button>
                </div>
            </div>
        </div>
    );
};

const ParentDashboard = (props: ParentDashboardProps) => {
    const { onNavigate, user, customGreeting, children, classes, students, homeworks, assignments, submissions, studentStats = [], notifications = [], joinRequests = [], onSaveChild, onUpdateChild, onDeleteChild, onUpdateSettings, onRefreshData, isLoading } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const [modalChild, setModalChild] = useState<Partial<Child> | null>(null);
    const [isTeacherDirectoryOpen, setIsTeacherDirectoryOpen] = useState(false);
    const [isFindClassOpen, setIsFindClassOpen] = useState(false);
    const [selectedChildForJoin, setSelectedChildForJoin] = useState<Child | null>(null);
    const [isSendingJoinRequest, setIsSendingJoinRequest] = useState(false);
    const [joinCodeFromLink, setJoinCodeFromLink] = useState<string>('');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const hasProcessedJoinLink = useRef(false);
    const [joinStudentIdFromLink, setJoinStudentIdFromLink] = useState<string | null>(null);
    
    const [welcomeChildInfo, setWelcomeChildInfo] = useState<{ name: string; targetClassName?: string } | null>(null);
    const [notifLimit, setNotifLimit] = useState(8);

    const ensureJoinCodeString = (v: unknown): string => {
        if (v == null) return '';
        if (typeof v === 'string') return v.trim();
        if (typeof v === 'object' && v !== null && 'joinCode' in (v as object)) return ensureJoinCodeString((v as { joinCode?: unknown }).joinCode);
        return '';
    };
    useEffect(() => {
        let joinCode = ensureJoinCodeString((location.state as any)?.joinCode);
        let joinStudentId = (location.state as any)?.joinStudentId;
        if (typeof joinStudentId !== 'string') joinStudentId = null;
        if (!joinCode) {
            try {
                const stored = sessionStorage.getItem('joinCodeFromLink');
                if (typeof stored === 'string') joinCode = stored.trim();
                const storedStudent = sessionStorage.getItem('joinStudentId');
                if (typeof storedStudent === 'string') joinStudentId = joinStudentId || storedStudent;
                if (joinCode) {
                    sessionStorage.removeItem('joinCodeFromLink');
                    if (joinStudentId) sessionStorage.removeItem('joinStudentId');
                }
            } catch { /* ignore */ }
        }
        if (joinCode && user?.role === 'parent' && !hasProcessedJoinLink.current) {
            hasProcessedJoinLink.current = true;
            setJoinCodeFromLink(joinCode);
            if (joinStudentId) setJoinStudentIdFromLink(joinStudentId);
            setIsFindClassOpen(true);
            if (children.length === 1) setSelectedChildForJoin(children[0]);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, user?.role, children, navigate, location.pathname]);

    const allChildClasses = useMemo(() => {
        const classMap = new Map<string, Class>();
        const norm = (s: string) => (s || '').trim().toLowerCase();

        children.forEach(child => {
            const childStudentId = child.studentId ?? (child as any).student_id;
            
            // Tìm studentId và các lớp từ joinRequests đã được duyệt
            const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id || r.childId) === String(child.id));
            const approvedReqs = childReqs.filter(r => r.status === 'approved');
            const approvedClassIds = approvedReqs.map(r => String(r.class_id || r.classId));
            const studentIdFromReq = approvedReqs.find(r => r.student_id || r.studentId)?.student_id || approvedReqs.find(r => r.student_id || r.studentId)?.studentId;

            const student = students.find(s => 
                (childStudentId != null && String(s.id) === String(childStudentId)) ||
                (studentIdFromReq != null && String(s.id) === String(studentIdFromReq)) ||
                // Tuyệt đối không khớp theo tên tự do để tránh nhầm lẫn dữ liệu khi chưa được phê duyệt
                (s.studentId && childStudentId && String(s.studentId) === String(childStudentId))
            );
            
            const studentIdStr = student ? String(student.id) : (childStudentId ? String(childStudentId) : (studentIdFromReq ? String(studentIdFromReq) : null));
            
            classes.forEach(cls => {
                const clsIdStr = String(cls.id);
                const isApproved = approvedClassIds.includes(clsIdStr);
                const hasStats = studentIdStr && studentStats && studentStats.some(s => String(s.studentId || s.student_id) === studentIdStr && String(s.classId || s.class_id) === clsIdStr);
                
                // Chỉ hiển thị lớp nếu đã được duyệt (approved) hoặc có dữ liệu học tập thực tế (stats)
                if (isApproved || hasStats) {
                    classMap.set(clsIdStr, cls);
                }
            });
        });
        return Array.from(classMap.values());
    }, [classes, children, students, joinRequests, studentStats]);

    const classNotifications = useMemo(() => {
        type NotifItem = { 
            type: 'assignment'|'graded'|'notification'|'group'; 
            title: string; 
            subtitle: string; 
            child: Child; 
            class?: Class; 
            date: string; 
            assignment?: Assignment; 
            submission?: Submission; 
            notification?: Notification;
            count?: number;
            items?: any[];
        };
        let items: NotifItem[] = [];
        const norm = (s: string) => (s || '').trim().toLowerCase();

        children.forEach(child => {
            const childStudentId = child.studentId ?? (child as any).student_id;
            
            // Tìm studentId và các lớp từ joinRequests đã được duyệt
            const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id || r.childId) === String(child.id));
            const approvedReqs = childReqs.filter(r => r.status === 'approved');
            const approvedClassIds = approvedReqs.map(r => String(r.class_id || r.classId));
            const studentIdFromReq = approvedReqs.find(r => r.student_id || r.studentId)?.student_id || approvedReqs.find(r => r.student_id || r.studentId)?.studentId;

            const student = students.find(s => 
                (childStudentId != null && String(s.id) === String(childStudentId)) ||
                (studentIdFromReq != null && String(s.id) === String(studentIdFromReq)) ||
                (s.studentId && childStudentId && String(s.studentId) === String(childStudentId))
            );
            
            if (!student) return;
            const studentIdStr = String(student.id);
            const childClasses = classes.filter(cls => {
                const clsIdStr = String(cls.id);
                const hasStats = studentStats && studentStats.some(s => String(s.studentId || s.student_id) === studentIdStr && String(s.classId || s.class_id) === clsIdStr);
                return approvedClassIds.includes(clsIdStr) || hasStats;
            });
            childClasses.forEach(cls => {
                const assnStudentIds = (a: any) => (a.studentIds || a.student_ids || []) as any[];
                const isForStudent = (a: any) => { const ids = assnStudentIds(a); return ids.length === 0 || ids.some((sid: any) => String(sid) === studentIdStr); };
                
                // Thu thập assignments cho lớp này - CHỈ HIỂN THỊ NẾU CHƯA NỘP
                assignments.filter(a => String(a.classId || (a as any).class_id) === String(cls.id) && a.status !== 'recalled' && isForStudent(a)).forEach(assn => {
                    const hasSubmitted = submissions.some(s => String(s.assignmentId) === String(assn.id) && String(s.studentId) === studentIdStr);
                    if (hasSubmitted && assn.type !== 'announcement') return;
                    
                    const hw = homeworks.find(h => h.id === assn.homeworkId);
                    const typeLabel = assn.type === 'announcement' ? 'Thông báo' : assn.type === 'task' ? 'Nhiệm vụ' : 'Bài tập';
                    const assnTitle = (assn as any).homeworkTitle || (assn as any).homework_title || assn.title || hw?.title || typeLabel;
                    items.push({
                        type: 'assignment',
                        title: assnTitle,
                        subtitle: `${cls.name} • ${child.name}`,
                        child,
                        class: cls,
                        date: assn.assignedDate || (assn as any).assigned_date || '',
                        assignment: assn
                    });
                });

                // Thu thập graded submissions
                submissions.filter(s => String(s.studentId) === studentIdStr && isSubmissionGraded(s.status)).forEach(sub => {
                    const assn = assignments.find(a => a.id === sub.assignmentId);
                    if (!assn || String(assn.classId || (assn as any).class_id) !== String(cls.id)) return;
                    const hw = homeworks.find(h => h.id === assn.homeworkId);
                    const gradedTitle = (assn as any).homeworkTitle || (assn as any).homework_title || hw?.title || assn.title || 'Bài tập';
                    items.push({
                        type: 'graded',
                        title: `Đã chấm: ${gradedTitle}`,
                        subtitle: `${cls.name} • ${child.name}`,
                        child,
                        class: cls,
                        date: sub.submittedDate || (sub as any).submitted_date || '',
                        submission: sub,
                        assignment: assn
                    });
                });
            });
        });

        // Bổ sung từ notifications
        if (notifications.length > 0) {
            // Chế độ seenMessages cần bao gồm cả tên con để tránh deduplicate sai khi nhiều con có cùng bài tập
            const seenMessages = new Set(items.map(i => i.title + i.subtitle));
            
            notifications.filter(n => (((n as any).type === 'assignment' || n.icon === 'homework' || (n.icon as any) === 'task' || (n.icon as any) === 'announcement') && (n.message || '').includes('lớp'))).forEach(notif => {
                const msg = notif.message || '';
                const m = msg.match(/trong lớp\s+(.+?)(?:\.|$)/) || msg.match(/lớp\s+(.+?)(?:\s|\.|$)/);
                const className = m ? m[1].trim() : '';
                const titleMatch = msg.match(/giao:\s*(.+?)\s+trong lớp/) || msg.match(/(.+?)\s+trong lớp/);
                const title = titleMatch ? titleMatch[1].trim() : msg.slice(0, 60);
                
                const cls = classes.find(c => c.name === className || c.name?.includes(className) || String(c.id) === String((notif as any).classId));
                if (!cls) return;

                // Lọc notification theo metadata hoặc tên con
                const nChildId = (notif as any).childId || (notif as any).child_id;
                let childForNotif = children.find(c => {
                    if (nChildId) return String(c.id) === String(nChildId);
                    const name = (c.name || '').toLowerCase();
                    const realName = (c.realName || '').toLowerCase();
                    const msgLower = msg.toLowerCase();
                    return (name && msgLower.includes(name)) || (realName && msgLower.includes(realName));
                });

                if (!childForNotif) {
                    childForNotif = children.find(c => {
                        const approvedIds = (joinRequests || [])
                            .filter((r: any) => String(r.child_id || r.childId) === String(c.id) && r.status === 'approved')
                            .map(r => String(r.class_id || r.classId));
                        return approvedIds.includes(String(cls.id));
                    });
                }
                
                if (!childForNotif) return;

                const subtitle = `${cls.name} • ${childForNotif.name}`;
                const key = title + subtitle;
                if (seenMessages.has(key)) return;

                seenMessages.add(key);
                items.push({
                    type: 'notification',
                    title,
                    subtitle,
                    child: childForNotif,
                    class: cls,
                    date: (notif as any).createdAt || (notif as any).created_at || notif.timestamp || new Date().toISOString(),
                    notification: notif
                });
            });
        }

        // Sắp xếp theo thời gian mới nhất
        items.sort((a, b) => (new Date(b.date || 0).getTime()) - (new Date(a.date || 0).getTime()));

        // GOM NHÓM THÔNG MINH: Nếu cùng một con, cùng một lớp, cùng một loại thông báo trong cùng một ngày
        const grouped: NotifItem[] = [];
        items.forEach(item => {
            const itemDate = new Date(item.date).toLocaleDateString('vi-VN');
            const lastGroup = grouped.length > 0 ? grouped[grouped.length - 1] : null;
            
            if (lastGroup && 
                lastGroup.type === item.type && 
                lastGroup.child.id === item.child.id && 
                lastGroup.class?.id === item.class?.id && 
                new Date(lastGroup.date).toLocaleDateString('vi-VN') === itemDate
            ) {
                // Thêm vào nhóm cũ
                if (!lastGroup.items) {
                    const firstItem = { ...lastGroup };
                    lastGroup.type = 'group';
                    lastGroup.items = [firstItem];
                    lastGroup.count = 1;
                }
                lastGroup.items!.push(item);
                lastGroup.count = lastGroup.items!.length;
                
                // Cập nhật tiêu đề nhóm
                if (item.type === 'assignment') {
                    lastGroup.title = `${lastGroup.count} bài tập/nhiệm vụ mới`;
                } else if (item.type === 'graded') {
                    lastGroup.title = `${lastGroup.count} bài tập đã được chấm điểm`;
                } else {
                    lastGroup.title = `${lastGroup.count} thông báo từ lớp`;
                }
            } else {
                grouped.push({ ...item });
            }
        });

        return grouped;
    }, [children, students, classes, assignments, homeworks, submissions, notifications, joinRequests, studentStats]);

    const getChildProgress = (child: Child) => {
        let childStudentId = child.studentId ?? (child as any).student_id;
        
        // Bổ sung: Tìm studentId và các lớp từ joinRequests đã được duyệt
        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id || r.childId) === String(child.id));
        const approvedReqs = childReqs.filter(r => r.status === 'approved');
        const approvedClassIds = approvedReqs.map(r => String(r.class_id || r.classId));
        const studentIdFromReq = approvedReqs.find(r => r.student_id || r.studentId)?.student_id || approvedReqs.find(r => r.student_id || r.studentId)?.studentId;

        if (!childStudentId && studentIdFromReq) {
            childStudentId = studentIdFromReq;
        }

        let student = students.find(s => 
            (childStudentId != null && String(s.id) === String(childStudentId)) ||
            (studentIdFromReq != null && String(s.id) === String(studentIdFromReq)) ||
            (s.studentId && childStudentId && String(s.studentId) === String(childStudentId))
        );

        // Fallback: Nếu vẫn không thấy nhưng ta có childStudentId, hãy tạo một mock student object 
        if (!student && childStudentId) {
            student = {
                id: String(childStudentId),
                name: child.name || 'Học sinh',
                realName: child.realName || ''
            } as any;
        }

        if (!student) return null;

        // Find classes this student belongs to (chuẩn hóa so sánh ID)
        const studentIdStr = String(student.id);
        const childClassesRaw = classes.filter(cls => {
            const clsIdStr = String(cls.id);
            const isApproved = approvedClassIds.includes(clsIdStr) || (cls.studentIds || []).some((sid: any) => String(sid) === studentIdStr);
            const hasStats = studentStats && studentStats.some(s => String(s.studentId || s.student_id) === studentIdStr && String(s.classId || s.class_id) === clsIdStr);
            
            // Chỉ tính toán tiến độ cho các lớp đã được phê duyệt hoặc có dữ liệu stats thực tế
            return isApproved || hasStats;
        });

        // Deduplicate classes to avoid unique key warnings and double counting
        const childClasses = Array.from(new Map(childClassesRaw.map(c => [String(c.id), c])).values());

        // Nếu không có lớp nào, không nên hiển thị stats (GPA/Chuyên cần) của các lớp cũ hoặc sai lệch
        if (childClasses.length === 0) {
            return {
                student,
                childClasses: [],
                recentSessions: [],
                avgGrade: '---',
                attendanceRate: '---'
            };
        }

        const allStudentSessions = childClasses.flatMap(cls => 
            cls.sessions?.map(s => ({ ...s, className: cls.name })) || []
        ).filter(s => (s.grades || []).some((g: any) => String(g.studentId || g.student_id) === studentIdStr));

        const recentSessions = allStudentSessions
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 3);

        // ƯU TIÊN: Dùng stats đã được tính toán sẵn từ backend cho CÁC LỚP HIỆN TẠI
        const currentClassIds = childClasses.map(c => String(c.id));
        const studentStatsForChild = studentStats ? studentStats.filter(s => 
            String(s.studentId || s.student_id) === studentIdStr && 
            currentClassIds.includes(String(s.classId || s.class_id))
        ) : [];

        if (studentStatsForChild.length > 0) {
            const validStats = studentStatsForChild.map(s => s.stats).filter(Boolean);
            if (validStats.length > 0) {
                // Tính trung bình có trọng số theo số lượng bài tập/buổi học nếu có thể, hoặc trung bình các lớp
                const totalAvg = validStats.reduce((acc, curr) => acc + (curr.avg_grade || 0), 0) / validStats.length;
                const totalAtt = validStats.reduce((acc, curr) => acc + (curr.attendance_rate || 0), 0) / validStats.length;
                
                return { 
                    student, 
                    childClasses, 
                    recentSessions, 
                    avgGrade: totalAvg.toFixed(1), 
                    attendanceRate: totalAtt.toFixed(0) 
                };
            }
        }

        const gradedSessions = allStudentSessions.filter(s => s.grades.find(g => String(g.studentId) === studentIdStr)?.grade !== '');
        
        // Bổ sung: Lấy cả điểm từ submissions để tính Điểm TB tổng hợp
        const studentSubmissions = submissions.filter(s => String(s.studentId) === studentIdStr && isSubmissionGraded(s.status) && s.grade != null);
        
        const allGrades: number[] = [];
        gradedSessions.forEach(sess => {
            const g = sess.grades.find(gr => String(gr.studentId) === studentIdStr);
            if (g && g.grade !== '' && g.grade != null) allGrades.push(parseFloat(String(g.grade)));
        });
        studentSubmissions.forEach(sub => {
            allGrades.push(parseFloat(String(sub.grade)));
        });

        const avgGrade = allGrades.length > 0 
            ? (allGrades.reduce((acc, g) => acc + g, 0) / allGrades.length).toFixed(1)
            : '---';

        const attendanceRate = allStudentSessions.length > 0
            ? (allStudentSessions.filter(sess => sess.grades.find(g => String(g.studentId) === studentIdStr)?.attendance === 'present').length / allStudentSessions.length * 100).toFixed(0)
            : '---';

        return { student, childClasses, recentSessions, avgGrade, attendanceRate };
    };

    /** Thống kê bài tập/nhiệm vụ từ assignments + submissions (để phụ huynh theo dõi đầy đủ) */
    const getChildAssignmentStats = (child: Child) => {
        let childStudentId = child.studentId ?? (child as any).student_id;
        
        // Bổ sung: Tìm studentId và các lớp từ joinRequests đã được duyệt
        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id || r.childId) === String(child.id));
        const approvedReqs = childReqs.filter(r => r.status === 'approved');
        const approvedClassIds = approvedReqs.map(r => String(r.class_id || r.classId));
        const studentIdFromReq = approvedReqs.find(r => r.student_id || r.studentId)?.student_id || approvedReqs.find(r => r.student_id || r.studentId)?.studentId;

        if (!childStudentId && studentIdFromReq) {
            childStudentId = studentIdFromReq;
        }

        const norm = (s: string) => (s || '').trim().toLowerCase();
        const partialNameMatch = (a: string, b: string) => a && b && (norm(a).includes(norm(b)) || norm(b).includes(norm(a)));

        let student = students.find(s => 
            (childStudentId != null && String(s.id) === String(childStudentId)) ||
            (studentIdFromReq != null && String(s.id) === String(studentIdFromReq)) ||
            (child.name && s.name && (norm(child.name) === norm(s.name) || partialNameMatch(child.name, s.name))) ||
            (child.realName && s.realName && (norm(child.realName) === norm(s.realName) || partialNameMatch(child.realName, s.realName))) ||
            (s.studentId && childStudentId && String(s.studentId) === String(childStudentId))
        );

        if (!student && childStudentId) {
            student = { id: String(childStudentId), name: child.name } as any;
        }

        if (!student) return null;
        const studentIdStr = String(student.id);
        const childClassIds = classes
            .filter(cls => {
                const clsIdStr = String(cls.id);
                const sidArray = (cls.studentIds || []).map(String);
                const isMember = sidArray.includes(studentIdStr);
                const isApproved = approvedClassIds.includes(clsIdStr);
                const hasStats = studentStats && studentStats.some(s => String(s.studentId || s.student_id) === studentIdStr && String(s.classId || s.class_id) === clsIdStr);
                return isMember || isApproved || hasStats;
            })
            .map(c => String(c.id));
            
        if (childClassIds.length === 0) return { total: 0, completed: 0, avgGrade: '---' };
        const now = new Date();
        const childAssignments = assignments.filter(a => {
            const aClassId = String(a.classId ?? (a as any).class_id ?? '');
            if (!childClassIds.includes(aClassId) || a.status === 'recalled') return false;
            const aStudentIds = (a.studentIds || (a as any).student_ids || []) as any[];
            if (aStudentIds.length > 0 && !aStudentIds.some((sid: any) => String(sid) === studentIdStr)) return false;
            const scheduled = a.scheduledDate || (a as any).scheduled_date;
            if (scheduled && new Date(scheduled) > now) return false;
            return true;
        });
        const childSubmissions = submissions.filter(s => String(s.studentId) === studentIdStr);
        const completed = childAssignments.filter(a => childSubmissions.some(s => String(s.assignmentId) === String(a.id))).length;
        const gradedSubs = childSubmissions.filter(s => isSubmissionGraded(s.status) && s.grade != null);
        const avgGrade = gradedSubs.length > 0
            ? (gradedSubs.reduce((acc, s) => acc + (parseFloat(String(s.grade)) || 0), 0) / gradedSubs.length).toFixed(1)
            : null;
        return { total: childAssignments.length, completed, avgGrade };
    };

    /** Kết quả học tập theo từng lớp - để hiển thị trên thẻ con */
    const getChildResultsByClass = (child: Child) => {
        let childStudentId = child.studentId ?? (child as any).student_id;
        
        // Bổ sung: Tìm studentId và các lớp từ joinRequests đã được duyệt
        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id || r.childId) === String(child.id));
        const approvedReqs = childReqs.filter(r => r.status === 'approved');
        const approvedClassIds = approvedReqs.map(r => String(r.class_id || r.classId));
        const studentIdFromReq = approvedReqs.find(r => r.student_id || r.studentId)?.student_id || approvedReqs.find(r => r.student_id || r.studentId)?.studentId;

        if (!childStudentId && studentIdFromReq) {
            childStudentId = studentIdFromReq;
        }

        const norm = (s: string) => (s || '').trim().toLowerCase();
        const partialNameMatch = (a: string, b: string) => a && b && (norm(a).includes(norm(b)) || norm(b).includes(norm(a)));

        const student = students.find(s =>
            (childStudentId != null && String(s.id) === String(childStudentId)) ||
            (studentIdFromReq != null && String(s.id) === String(studentIdFromReq)) ||
            (child.name && s.name && (norm(child.name) === norm(s.name) || partialNameMatch(child.name, s.name))) ||
            (child.realName && s.realName && (norm(child.realName) === norm(s.realName) || partialNameMatch(child.realName, s.realName))) ||
            (s.studentId && childStudentId && String(s.studentId) === String(childStudentId))
        );
        if (!student) return [];
        const studentIdStr = String(student.id);
        const childClassesRaw = classes.filter(cls => {
            const clsIdStr = String(cls.id);
            const sidArray = (cls.studentIds || []).map(String);
            const isMember = sidArray.includes(studentIdStr);
            const isApproved = approvedClassIds.includes(clsIdStr);
            const hasStats = studentStats && studentStats.some(s => String(s.studentId || s.student_id) === studentIdStr && String(s.classId || s.class_id) === clsIdStr);
            return isMember || isApproved || hasStats;
        });

        // Loại bỏ các lớp bị trùng lặp (nếu có) để tránh lỗi duplicate key trong React
        const childClasses = Array.from(new Map(childClassesRaw.map(c => [String(c.id), c])).values());

        return childClasses.map(cls => {
            const clsIdStr = String(cls.id);
            
            // ƯU TIÊN: Dùng stats đã được tính toán sẵn từ backend cho lớp này
            const precomputed = studentStats.find(s => String(s.classId) === clsIdStr && String(s.studentId) === studentIdStr);
            if (precomputed && precomputed.stats) {
                const s = precomputed.stats;
                return { 
                    class: cls, 
                    totalAssignments: s.total_assignments || 0, 
                    completed: s.completed_assignments || 0, 
                    avgGrade: s.avg_grade != null ? s.avg_grade.toFixed(1) : null, 
                    sessionAvgGrade: null, // Không cần phân tách nữa
                    attendance: s.attendance_rate != null ? s.attendance_rate.toFixed(0) : null, 
                    notificationCount: classNotifications.filter(n => String(n.child.id) === String(child.id) && n.class && String(n.class.id) === clsIdStr).length 
                };
            }

            const classAssignments = assignments.filter(a => {
                const aClassId = String(a.classId ?? (a as any).class_id ?? '');
                if (aClassId !== clsIdStr || a.status === 'recalled') return false;
                const aStudentIds = (a.studentIds || (a as any).student_ids || []) as any[];
                return aStudentIds.length === 0 || aStudentIds.some((sid: any) => String(sid) === studentIdStr);
            });
            const classSubmissions = submissions.filter(s =>
                String(s.studentId) === studentIdStr &&
                classAssignments.some(a => String(a.id) === String(s.assignmentId))
            );
            const completed = classAssignments.filter(a =>
                classSubmissions.some(s => String(s.assignmentId) === String(a.id))
            ).length;
            const gradedSubs = classSubmissions.filter(s => isSubmissionGraded(s.status) && s.grade != null);
            
            const clsSessions = cls.sessions || [];
            const mySessions = clsSessions.filter((s: any) => (s.grades || []).some((g: any) => String(g.studentId) === studentIdStr));
            
            // Tính Điểm TB tổng hợp cho riêng lớp này (Sessions + Submissions)
            const classAllGrades: number[] = [];
            gradedSubs.forEach(s => classAllGrades.push(parseFloat(String(s.grade))));
            mySessions.forEach((s: any) => {
                const g = (s.grades || []).find((gr: any) => String(gr.studentId) === studentIdStr);
                if (g && g.grade !== '' && g.grade != null) classAllGrades.push(parseFloat(String(g.grade)));
            });

            const combinedAvgGrade = classAllGrades.length > 0
                ? (classAllGrades.reduce((acc, g) => acc + g, 0) / classAllGrades.length).toFixed(1)
                : null;

            const avgGrade = gradedSubs.length > 0
                ? (gradedSubs.reduce((acc, s) => acc + (parseFloat(String(s.grade)) || 0), 0) / gradedSubs.length).toFixed(1)
                : null;
            
            const sessionAvgGrade = mySessions.filter((s: any) => {
                const g = (s.grades || []).find((gr: any) => String(gr.studentId) === studentIdStr);
                return g && g.grade !== '' && g.grade != null;
            }).length > 0
                ? (mySessions
                    .map((s: any) => (s.grades || []).find((gr: any) => String(gr.studentId) === studentIdStr)?.grade)
                    .filter((g: any) => g !== '' && g != null)
                    .reduce((acc: number, g: any) => acc + (parseFloat(String(g)) || 0), 0) /
                    mySessions.filter((s: any) => {
                        const g = (s.grades || []).find((gr: any) => String(gr.studentId) === studentIdStr);
                        return g && g.grade !== '' && g.grade != null;
                    }).length).toFixed(1)
                : null;
            const attendance = mySessions.length > 0
                ? (mySessions.filter((s: any) => (s.grades || []).find((gr: any) => String(gr.studentId) === studentIdStr)?.attendance === 'present').length / mySessions.length * 100).toFixed(0)
                : null;

            return { class: cls, totalAssignments: classAssignments.length, completed, avgGrade: combinedAvgGrade, sessionAvgGrade, attendance, notificationCount: classNotifications.filter(n => String(n.child.id) === String(child.id) && n.class && String(n.class.id) === clsIdStr).length };
        });
    };

    const limits = getUserLimits(user);
    const canAddMoreChildren = checkLimit(children.length, limits.maxChildren);

    const handleOpenAddChild = () => {
        if (!canAddMoreChildren) {
            alert(`Bạn đã đạt giới hạn tối đa ${limits.maxChildren} tài khoản con. Vui lòng nâng cấp gói để tiếp tục.`);
            onNavigate(View.CreditStore);
            return;
        }
        setModalChild({});
    };

    const handleSaveChild = (childToSave: Child) => {
        const { id, ...data } = childToSave;
        const isNew = !id || id.toString().startsWith('temp_');
        
        if (isNew && !checkLimit(children.length, limits.maxChildren)) {
            alert(`Bạn đã đạt giới hạn tối đa ${limits.maxChildren} hồ sơ con. Vui lòng nâng cấp gói để tiếp tục.`);
            onNavigate(View.CreditStore);
            return;
        }

        if (!isNew) {
            onUpdateChild(childToSave);
        } else {
            onSaveChild(data);
        }
        setModalChild(null);
    };

    const handleDeleteChild = (childId: string | number) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa tài khoản này?")) {
            onDeleteChild(childId);
        }
    };

    return (
        <div className="space-y-6">
            <HelpOnboarding steps={parentHelpSteps} role="parent" />
            {welcomeChildInfo && (
                <NewChildWelcomeModal 
                    isOpen={!!welcomeChildInfo} 
                    onClose={() => setWelcomeChildInfo(null)} 
                    childName={welcomeChildInfo.name} 
                    targetClassName={welcomeChildInfo.targetClassName}
                    onNavigate={onNavigate} 
                />
            )}
            {modalChild && <ChildAccountModal child={modalChild} onClose={() => setModalChild(null)} onSave={handleSaveChild} />}
            {isTeacherDirectoryOpen && (
                <TeacherDirectoryModal 
                    isOpen={isTeacherDirectoryOpen} 
                    onClose={() => setIsTeacherDirectoryOpen(false)} 
                    childClasses={allChildClasses} 
                />
            )}
            {isFindClassOpen && (
                <FindClassModal 
                    isOpen={isFindClassOpen} 
                    onClose={() => { setIsFindClassOpen(false); setSelectedChildForJoin(null); setJoinCodeFromLink(''); setJoinStudentIdFromLink(null); }} 
                    navigate={navigate}
                    initialCode={joinCodeFromLink}
                    initialStudentId={joinStudentIdFromLink}
                    onJoin={async (targetClass, student, childId, extraInfo) => {
                        const effectiveChildId = childId === null ? undefined : (childId ?? selectedChildForJoin?.id);
                        const childName = selectedChildForJoin?.name ?? extraInfo?.childDisplayName ?? student?.name ?? 'con';

                        if (!effectiveChildId && !student?.id && !(extraInfo?.childDisplayName?.trim())) {
                            alert('Vui lòng chọn học sinh trong danh sách lớp hoặc điền tên con khi không tìm thấy.');
                            return;
                        }

                        if (extraInfo?.updateProfile && onUpdateSettings) {
                            onUpdateSettings(prev => prev ? { 
                                ...prev, 
                                teacherName: extraInfo.parentName, 
                                phone: extraInfo.parentPhone 
                            } : null);
                        }

                        if (APP_MODE === 'PRODUCTION' && targetClass) {
                            setIsSendingJoinRequest(true);
                            try {
                                const message = extraInfo 
                                    ? `Yêu cầu từ: ${extraInfo.parentName} (${extraInfo.relationship}). Tên học sinh thực tế: ${extraInfo.childDisplayName}. SĐT: ${extraInfo.parentPhone}. Lớp: ${targetClass.name}`
                                    : (student 
                                        ? `Phụ huynh muốn kết nối con ${childName} với hồ sơ ${student.name}. Lớp: ${targetClass.name}`
                                        : `Phụ huynh đăng ký con ${childName} tham gia lớp mới (chưa có hồ sơ). Lớp: ${targetClass.name}`);

                                const result = await createJoinRequest({
                                    child_id: effectiveChildId ? Number(effectiveChildId) : undefined,
                                    class_id: Number(targetClass.id),
                                    student_id: student?.id ? Number(student.id) : undefined,
                                    message: message,
                                    parent_info: extraInfo ? { name: extraInfo.parentName, phone: extraInfo.parentPhone, relationship: extraInfo.relationship } : undefined,
                                    student_info: extraInfo 
                                        ? { 
                                            name: extraInfo.childDisplayName, 
                                            real_name: (student as any)?.realName,
                                            dob: extraInfo.childDob,
                                            gender: extraInfo.childGender
                                          } 
                                        : (student ? { name: student.name, real_name: (student as any)?.realName, dob: (student as any)?.dob } : undefined),
                                });
                                if (result.success) {
                                    let successMsg = '';
                                    if (result.isDuplicate) {
                                        successMsg = result.message || 'Yêu cầu tham gia đã được gửi trước đó và đang chờ giáo viên phê duyệt.';
                                    } else {
                                        const baseMsg = student 
                                            ? `Đã gửi yêu cầu kết nối con ${childName} với hồ sơ học sinh "${student.name}" tại lớp "${targetClass.name}".`
                                            : `Đã gửi yêu cầu đăng ký con ${childName} tham gia lớp "${targetClass.name}".`;
                                        
                                        if (effectiveChildId) {
                                            successMsg = `${baseMsg} Vui lòng chờ giáo viên phê duyệt.`;
                                        } else {
                                            // successMsg = `Hồ sơ của con đã được tạo thành công! ${baseMsg} Bạn có thể quản lý con tại bảng điều khiển trong khi chờ giáo viên phê duyệt kết nối lớp.`;
                                            setWelcomeChildInfo({ 
                                                name: childName, 
                                                targetClassName: targetClass?.name 
                                            });
                                            setIsFindClassOpen(false);
                                            setSelectedChildForJoin(null);
                                            onRefreshData?.();
                                            return; // Bỏ qua alert mặc định
                                        }
                                    }
                                    alert(successMsg);
                                    setIsFindClassOpen(false);
                                    setSelectedChildForJoin(null);
                                    onRefreshData?.();
                                } else {
                                    alert(result.message || 'Không thể gửi yêu cầu. Vui lòng thử lại sau.');
                                }
                            } catch (e) {
                                alert('Lỗi khi gửi yêu cầu tham gia.');
                            } finally {
                                setIsSendingJoinRequest(false);
                            }
                        } else {
                            alert(`Đã gửi yêu cầu kết nối con ${childName} với hồ sơ ${student?.name || 'mới'} tại lớp "${targetClass.name}". Vui lòng chờ giáo viên phê duyệt.`);
                            setIsFindClassOpen(false);
                            setSelectedChildForJoin(null);
                        }
                    }} 
                    selectedChild={selectedChildForJoin}
                    children={children}
                    onSelectChild={setSelectedChildForJoin}
                    classes={classes}
                    students={students}
                    joinRequests={joinRequests}
                    user={user}
                    isSending={isSendingJoinRequest}
                    limits={limits}
                    onSaveChild={onSaveChild}
                />
            )}

            <section id="parent-welcome-section" className="relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
                <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-3">
                            {getDashboardGreeting('parent', user.teacherName || user.email || '', customGreeting)}
                            <button 
                                onClick={() => onNavigate(View.AffiliateDashboard)}
                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-xs font-black uppercase tracking-widest transition-all border border-white/10 group/income active:scale-95"
                                title="Tham gia chương trình Affiliate để tăng thu nhập cùng Bookai"
                            >
                                <WalletIcon className="h-3.5 w-3.5 text-amber-300 group-hover/income:scale-110 transition-transform" />
                                <span className="hidden sm:inline">Tăng thu nhập</span>
                                <span className="sm:hidden">Affiliate</span>
                            </button>
                        </h2>
                        <p className="mt-1.5 text-sm text-teal-100/90 max-w-xl">{getDashboardSubtitle('parent', { childrenCount: children.length })}</p>
                        <div className="mt-4 flex gap-2">
                            <button
                                onClick={handleOpenAddChild}
                                title={canAddMoreChildren ? undefined : `Đã đạt giới hạn ${limits.maxChildren} tài khoản con — bấm để nâng cấp`}
                                className={`px-5 py-2 rounded-xl text-sm font-bold transition-all shadow-lg active:scale-[0.98] ${
                                    canAddMoreChildren
                                        ? 'bg-white text-teal-700 hover:bg-teal-50'
                                        : 'bg-white/80 text-amber-700 hover:bg-amber-50 ring-2 ring-amber-200/80'
                                }`}
                            >
                                {canAddMoreChildren ? '+ Thêm con' : `Đã đủ ${limits.maxChildren} con · Nâng cấp`}
                            </button>
                            <button
                                onClick={async () => {
                                    if (!onRefreshData) return;
                                    setIsRefreshing(true);
                                    try { await onRefreshData(); } finally { setIsRefreshing(false); }
                                }}
                                disabled={isRefreshing}
                                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 rounded-xl transition-all disabled:opacity-60 text-sm font-bold"
                                title="Làm mới dữ liệu bài tập và thông báo"
                            >
                                <ArrowPathIcon className={`h-5 w-5 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
                                Làm mới
                            </button>
                        </div>
                    </div>
                    {children.length > 0 && (
                        <div className="flex flex-wrap gap-2 justify-end">
                            {children.slice(0, 4).map(c => (
                                <div key={c.id} className="flex items-center gap-2.5 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-xl border border-white/20 shrink-0 shadow-sm hover:bg-white/20 transition-colors">
                                    {c.avatarUrl ? (
                                        <img src={formatAssetUrl(c.avatarUrl)} alt={c.name} className="w-8 h-8 rounded-full object-cover shadow-sm border border-white/20" />
                                    ) : (
                                        <span className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-sm font-bold text-teal-700 shadow-sm">{c.name.charAt(0)}</span>
                                    )}
                                    <span className="text-sm font-semibold text-white">{c.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
                <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-cyan-400/10 rounded-full blur-2xl" />
            </section>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:gap-8">
                {/* Left Column: Children List & Quick Stats */}
                <div className="xl:col-span-2 space-y-6">
                    {/* Hiển thị các yêu cầu tham gia lớp đang chờ duyệt */}
                    {joinRequests && joinRequests.filter(r => r.status === 'pending').length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-sm font-black text-amber-600 uppercase tracking-widest flex items-center gap-2">
                                <ClockIcon className="h-4 w-4" /> Yêu cầu đang chờ giáo viên duyệt ({joinRequests.filter(r => r.status === 'pending').length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {joinRequests.filter(r => r.status === 'pending').map((req, idx) => {
                                    const targetClass = classes.find(c => String(c.id) === String(req.class_id || req.classId));
                                    const studentName = req.studentName || req.childName || req.student_info_json?.name || req.student_info_json?.real_name || (req.student && req.student.name) || 'Con';
                                    
                                    return (
                                        <div key={req.id || idx} className="bg-amber-50/50 border-2 border-amber-100 rounded-2xl p-4 flex items-center gap-4 relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 p-1.5 bg-amber-100 rounded-bl-xl text-[8px] font-black text-amber-700 uppercase tracking-tighter">Đang chờ</div>
                                            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-amber-500 shadow-sm border border-amber-100 shrink-0 font-black text-xl">
                                                {studentName.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-black text-slate-800 truncate">{studentName}</p>
                                                <p className="text-[11px] font-bold text-slate-500 mt-0.5">Tham gia: <span className="text-teal-700">{targetClass?.name || 'Đang tải...'}</span></p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <div className="h-1 flex-1 bg-amber-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-amber-400 w-1/3 animate-pulse" />
                                                    </div>
                                                    <span className="text-[9px] font-black text-amber-600 uppercase">Chờ duyệt</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                                <UserGroupIcon className="h-4 w-4 text-teal-600" />
                            </span>
                            Tiến độ học tập
                        </h3>
                    </div>
                    
                    {children.length === 0 ? (
                        <Card className="p-10 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                            <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <PlusIcon className="h-8 w-8 text-teal-500" />
                            </div>
                            <p className="text-slate-600 font-medium text-sm">Bạn chưa thêm tài khoản cho con.</p>
                            <button
                                onClick={handleOpenAddChild}
                                className={`mt-4 px-5 py-2 rounded-xl text-sm font-bold transition-colors ${
                                    canAddMoreChildren
                                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                                        : 'bg-amber-500 text-white hover:bg-amber-600'
                                }`}
                            >
                                {canAddMoreChildren ? 'Thêm con ngay' : `Nâng cấp để thêm con (giới hạn ${limits.maxChildren})`}
                            </button>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {children.map(child => {
                                const progress = getChildProgress(child);
                                const assignmentStats = getChildAssignmentStats(child);
                                const childResultsByClass = getChildResultsByClass(child);
                                const uniqueClasses = progress?.childClasses ? [...new Map(progress.childClasses.map(c => [String(c.id), c])).values()] : [];
                                const completionRate = assignmentStats ? (assignmentStats.total > 0 ? (assignmentStats.completed / assignmentStats.total) * 100 : 0) : 0;
                                
                                return (
                                    <Card key={child.id} className="overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-300 group rounded-2xl bg-white">
                                        <div className="p-0">
                                            {/* Card Header with Background */}
                                            <div className="relative h-24 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 p-5 flex justify-between items-start">
                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div className="relative">
                                                        <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-2xl flex items-center justify-center text-xl font-black shrink-0 shadow-lg group-hover:scale-105 transition-transform overflow-hidden">
                                                            {child.avatarUrl ? (
                                                                <img src={formatAssetUrl(child.avatarUrl)} alt={child.name} className="w-full h-full object-cover" />
                                                            ) : (
                                                                child.name.charAt(0)
                                                            )}
                                                        </div>
                                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                                                            <div className={`w-3.5 h-3.5 rounded-full ${completionRate >= 80 ? 'bg-emerald-500' : completionRate >= 50 ? 'bg-amber-400' : 'bg-rose-500'} animate-pulse`} />
                                                        </div>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="text-lg font-black text-slate-800 truncate leading-tight group-hover:text-teal-700 transition-colors">{child.name}</h4>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="px-2 py-0.5 bg-white/60 backdrop-blur-md rounded-md text-[10px] font-bold text-slate-600 border border-slate-200/50 shadow-sm">
                                                                {calculateAge(child.dob)} tuổi • {child.gender === 'male' ? 'Nam' : 'Nữ'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 shrink-0 bg-white/40 backdrop-blur-md p-1 rounded-xl border border-white/40 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                                                    <button onClick={() => setModalChild(child)} className="p-2 text-slate-500 hover:text-teal-600 hover:bg-white rounded-lg transition-all" title="Chỉnh sửa"><PencilIcon className="h-4 w-4"/></button>
                                                    <button onClick={() => handleDeleteChild(child.id)} className="p-2 text-slate-500 hover:text-rose-500 hover:bg-white rounded-lg transition-all" title="Xóa"><TrashIcon className="h-4 w-4"/></button>
                                                </div>
                                            </div>

                                            <div className="p-5 space-y-5">
                                                {progress && uniqueClasses.length > 0 ? (
                                                    <>
                                                        {/* Summary Stats Grid */}
                                                        <div className="grid grid-cols-3 gap-2 md:gap-3">
                                                            <div className="flex flex-col items-center p-2 md:p-3 bg-slate-50 rounded-2xl border border-slate-100/50 hover:bg-teal-50 hover:border-teal-100 transition-all cursor-default">
                                                                <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Hoàn thành</p>
                                                                <ProgressRing progress={completionRate} size={42} mdSize={54} color={completionRate >= 80 ? 'text-emerald-500' : 'text-teal-600'} />
                                                            </div>
                                                            <div className="flex flex-col items-center justify-center p-2 md:p-3 bg-slate-50 rounded-2xl border border-slate-100/50 hover:bg-emerald-50 hover:border-emerald-100 transition-all cursor-default text-center">
                                                                <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Điểm TB</p>
                                                                <div className="text-base md:text-xl font-black text-emerald-600">{progress.avgGrade}</div>
                                                                <p className="text-[7px] md:text-[8px] font-bold text-emerald-500/70 mt-0.5">Xếp loại tốt</p>
                                                            </div>
                                                            <div className="flex flex-col items-center justify-center p-2 md:p-3 bg-slate-50 rounded-2xl border border-slate-100/50 hover:bg-sky-50 hover:border-sky-100 transition-all cursor-default text-center">
                                                                <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Chuyên cần</p>
                                                                <div className="text-base md:text-xl font-black text-sky-600">{progress.attendanceRate}%</div>
                                                                <p className="text-[7px] md:text-[8px] font-bold text-sky-500/70 mt-0.5">Đều đặn</p>
                                                            </div>
                                                        </div>

                                                        {/* Class Wise Intelligent Summary */}
                                                        {childResultsByClass.length > 0 && (
                                                            <div className="space-y-3">
                                                                <div className="flex items-center justify-between">
                                                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Tình hình theo lớp ({childResultsByClass.length})</p>
                                                                    <button onClick={() => onNavigate(View.ChildDashboard, { child, focusSection: 'progress' })} className="text-[10px] font-black text-teal-600 hover:underline">Tất cả lớp →</button>
                                                                </div>
                                                                <div className="grid grid-cols-1 gap-2">
                                                                    {childResultsByClass.slice(0, 3).map(({ class: cls, totalAssignments, completed, avgGrade, sessionAvgGrade, attendance, notificationCount }) => {
                                                                        const classProgress = totalAssignments > 0 ? (completed / totalAssignments) * 100 : 0;
                                                                        return (
                                                                            <div key={cls.id} className="flex flex-col gap-2 p-3 bg-white rounded-xl border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all group/item">
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                                        <div className="w-1.5 h-4 bg-teal-500 rounded-full shrink-0" />
                                                                                        <h5 className="text-xs font-black text-slate-700 truncate" title={cls.name}>{cls.name}</h5>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        {notificationCount > 0 && (
                                                                                            <span className="flex items-center justify-center min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[8px] font-black rounded-full shadow-sm animate-pulse" title={`${notificationCount} thông báo mới`}>
                                                                                                {notificationCount}
                                                                                            </span>
                                                                                        )}
                                                                                        <span className={`text-[9px] font-bold ${classProgress === 100 ? 'text-emerald-500' : 'text-slate-400'}`}>
                                                                                            {completed}/{totalAssignments} BT
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                                                                                    <div 
                                                                                        className={`h-full transition-all duration-700 ${classProgress >= 80 ? 'bg-emerald-500' : 'bg-teal-500'}`} 
                                                                                        style={{ width: `${classProgress}%` }}
                                                                                    />
                                                                                </div>

                                                                                <div className="flex items-center gap-3">
                                                                                    <span className="flex items-center gap-1 text-[9px] font-black text-emerald-600 uppercase" title="Điểm trung bình">
                                                                                        <AcademicCapIcon className="h-2.5 w-2.5" /> {avgGrade || sessionAvgGrade || '---'}/10
                                                                                    </span>
                                                                                    <span className="flex items-center gap-1 text-[9px] font-black text-sky-600 uppercase" title="Tỉ lệ chuyên cần">
                                                                                        <CheckBadgeIcon className="h-2.5 w-2.5" /> {attendance || '---'}%
                                                                                    </span>
                                                                                    {classProgress === 100 && (
                                                                                        <span className="flex items-center gap-0.5 text-[8px] font-black text-emerald-500 uppercase bg-emerald-50 px-1 rounded">
                                                                                            <CheckIcon className="h-2 w-2" /> Hoàn thành
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    /* Join Request Status logic remains but visually improved */
                                                    <div className="py-6 px-5 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200 text-center flex flex-col items-center">
                                                        {(() => {
                                                            const getClassName = (r: any) => r.className || r.class_name || (() => {
                                                                const m = (r.message || '').match(/Lớp:\s*(.+?)(?:\.|$)/);
                                                                return m ? m[1].trim() : `Lớp #${r.classId || r.class_id}`;
                                                            })();
                                                            // Đảm bảo khớp cả camelCase và snake_case cho ID
                                                            const childReqs = (joinRequests || []).filter((r: any) => 
                                                                String(r.childId || r.child_id) === String(child.id)
                                                            );
                                                            const classList = childReqs.map(r => ({ 
                                                                name: getClassName(r), 
                                                                status: r.status, 
                                                                reason: r.rejectReason || r.reject_reason,
                                                                date: r.createdAt || r.created_at
                                                            }));

                                                            if (classList.length > 0) {
                                                                const pendingClasses = classList.filter(c => c.status === 'pending');
                                                                const rejectedClasses = classList.filter(c => c.status === 'rejected');
                                                                
                                                                return (
                                                                    <div className="w-full space-y-4">
                                                                        <div className="flex flex-col items-center gap-2 mb-2">
                                                                            <div className="relative">
                                                                                <div className="w-12 h-12 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center">
                                                                                    <UserGroupIcon className="h-6 w-6 text-sky-500" />
                                                                                </div>
                                                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-white animate-bounce" />
                                                                            </div>
                                                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Trạng thái kết nối</p>
                                                                        </div>

                                                                        <div className="space-y-2 max-h-[180px] overflow-y-auto custom-scrollbar pr-1">
                                                                            {classList.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((req, idx) => (
                                                                                <div key={idx} className="p-3 bg-white rounded-xl border border-slate-100 shadow-sm text-left group hover:border-teal-200 transition-all">
                                                                                    <div className="flex justify-between items-start gap-2">
                                                                                        <div className="min-w-0">
                                                                                            <p className="text-xs font-black text-slate-700 truncate">{req.name}</p>
                                                                                            <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 flex items-center gap-1">
                                                                                                {req.status === 'pending' ? 'Đang chờ phê duyệt' : 'Bị từ chối'}
                                                                                                {req.date && <span className="normal-case font-medium opacity-60">• {new Date(req.date).toLocaleDateString('vi-VN')}</span>}
                                                                                            </p>
                                                                                        </div>
                                                                                        <span className={`shrink-0 px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-tighter ${
                                                                                            req.status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600'
                                                                                        }`}>
                                                                                            {req.status === 'pending' ? 'Chờ duyệt' : 'Từ chối'}
                                                                                        </span>
                                                                                    </div>
                                                                                    {req.status === 'rejected' && req.reason && (
                                                                                        <div className="mt-2 p-2 bg-rose-50 rounded-lg text-[9px] font-medium text-rose-700 border border-rose-100/50 leading-relaxed italic">
                                                                                            Lý do: {req.reason}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>

                                                                        <div className="pt-2">
                                                                            <button 
                                                                                onClick={() => { setSelectedChildForJoin(child); setIsFindClassOpen(true); }} 
                                                                                className="w-full py-2.5 bg-white border-2 border-slate-100 text-teal-600 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-teal-50 hover:border-teal-200 transition-all active:scale-95 shadow-sm"
                                                                            >
                                                                                Kết nối thêm lớp +
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <div className="w-14 h-14 bg-white rounded-3xl shadow-sm border border-slate-100 flex items-center justify-center mb-4 group-hover:rotate-12 transition-transform">
                                                                        <UserPlusIcon className="h-7 w-7 text-slate-300" />
                                                                    </div>
                                                                    <h5 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-1">Chưa có lớp học</h5>
                                                                    <p className="text-[10px] text-slate-400 font-bold mb-5 leading-relaxed max-w-[180px]">Kết nối con với lớp học để giáo viên có thể giao bài và chấm điểm.</p>
                                                                    <button onClick={() => { setSelectedChildForJoin(child); setIsFindClassOpen(true); }} className="px-6 py-2.5 bg-teal-600 text-white rounded-xl text-[11px] font-black uppercase tracking-wider hover:bg-teal-700 transition-all shadow-lg active:scale-95 shadow-teal-100">
                                                                        Kết nối ngay
                                                                    </button>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                {/* Bottom Action Section - More Intelligent */}
                                                <div className="pt-4 border-t border-slate-100">
                                                    <div className="flex gap-2">
                                                        <button 
                                                            onClick={() => onNavigate(View.ChildDashboard, { child, focusSection: 'progress' })} 
                                                            className="flex-1 h-11 bg-white border-2 border-slate-100 text-slate-700 text-[11px] font-black uppercase tracking-widest rounded-xl hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                                        >
                                                            <ChartBarIcon className="h-4 w-4" />
                                                            Chi tiết
                                                        </button>
                                                        <button 
                                                            onClick={() => onNavigate(View.ChildDashboard, { child, focusSection: 'homework' })} 
                                                            className="flex-[1.5] h-11 bg-teal-600 text-white text-[11px] font-black uppercase tracking-widest rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                                        >
                                                            <BookOpenIcon className="h-4 w-4" />
                                                            Học bài ngay
                                                        </button>
                                                        <button 
                                                            onClick={() => setIsTeacherDirectoryOpen(true)} 
                                                            className="w-11 h-11 bg-white border-2 border-slate-100 text-slate-500 rounded-xl hover:bg-teal-50 hover:border-teal-200 hover:text-teal-600 transition-all active:scale-[0.98] flex items-center justify-center shrink-0"
                                                            title="Liên hệ giáo viên"
                                                        >
                                                            <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    )}

                    {/* Luyện đề cho con (Quiz Creator V2 - Phase 1) */}
                    {children.length > 0 && (
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                                    <span className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                                        <ClipboardDocumentCheckIcon className="h-4 w-4 text-teal-600" />
                                    </span>
                                    Luyện đề cho con
                                </h3>
                            </div>
                            <p className="text-xs text-slate-500 -mt-1">
                                Tạo đề ôn tập sát chương trình con đang học. Hệ thống tự bám lớp & bộ sách của con — bạn chỉ cần chọn mục đích và bấm tạo. Mỗi câu đều có đáp án + lời giải chi tiết.
                            </p>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                {[
                                    {
                                        preset: 'P_ON_CHUYEN_DE_CHO_CON',
                                        icon: '📖',
                                        title: 'Ôn theo chuyên đề',
                                        desc: 'Bám sát chương con đang học',
                                    },
                                    {
                                        preset: 'P_ON_HE',
                                        icon: '☀️',
                                        title: 'Đề ôn hè',
                                        desc: 'Củng cố + làm quen lớp tới',
                                    },
                                    {
                                        preset: 'P_KIEM_TRA_NHANH',
                                        icon: '🔍',
                                        title: 'Kiểm tra nhanh',
                                        desc: '10 phút đo lỗ hổng',
                                    },
                                    {
                                        preset: 'P_THI_THU_CHO_CON',
                                        icon: '🎯',
                                        title: 'Đề thi thử',
                                        desc: 'Mô phỏng đề thật của trường',
                                    },
                                ].map(p => (
                                    <button
                                        key={p.preset}
                                        type="button"
                                        onClick={() => onNavigate(View.QuizCreator, {
                                            // Bọc trong `featurePayload` để khớp pattern QuizCreator hiện đang đọc
                                            // qua `props.featurePayload?.purposePreset` (tránh phá tương thích).
                                            featurePayload: {
                                                purposePreset: p.preset,
                                                childId: children[0]?.id ? String(children[0].id) : undefined,
                                            },
                                        })}
                                        className="text-left p-4 rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 via-white to-cyan-50 shadow-sm hover:shadow-lg hover:border-teal-300 hover:-translate-y-0.5 transition-all duration-300 group"
                                    >
                                        <div className="flex items-start gap-2.5">
                                            <span className="h-10 w-10 rounded-xl bg-white border border-teal-100 flex items-center justify-center text-2xl shrink-0 group-hover:scale-105 transition-transform">
                                                <span aria-hidden>{p.icon}</span>
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-sm font-black text-slate-800 group-hover:text-teal-700 transition-colors">{p.title}</h4>
                                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 leading-snug">{p.desc}</p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Định hướng nghề nghiệp & hồ sơ phát triển con */}
                    <div className="space-y-4 pt-2">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                                    <FlagIcon className="h-4 w-4 text-violet-600" />
                                </span>
                                Định hướng & Hành trình khám phá
                            </h3>
                        </div>
                        <p className="text-xs text-slate-500 -mt-1">
                            Tạo hồ sơ khám phá nghề nghiệp cho từng con, theo dõi sở thích, điểm mạnh và đề xuất lộ trình học tập phù hợp với độ tuổi.
                        </p>
                        <button
                            type="button"
                            onClick={() => onNavigate(View.CareerJourney)}
                            className="w-full text-left p-4 sm:p-5 rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50 via-white to-pink-50 shadow-sm hover:shadow-lg hover:border-violet-300 hover:-translate-y-0.5 transition-all duration-300 group"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center shrink-0 text-white group-hover:scale-105 transition-transform">
                                        <FlagIcon className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-sm font-black text-slate-800 group-hover:text-violet-700 transition-colors">
                                            Hành trình khám phá nghề nghiệp
                                        </h4>
                                        <p className="text-xs text-slate-600 mt-0.5">
                                            Tạo hồ sơ cho con (Tiểu học → Sinh viên), AI gợi ý nghề phù hợp + lộ trình học tập 5 phase. Dùng AI nâng cao chỉ 10 credit / lần phân tích sâu.
                                        </p>
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            <span className="px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-bold">Tiểu học</span>
                                            <span className="px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold">THCS</span>
                                            <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">THPT</span>
                                            <span className="px-2 py-0.5 rounded-full bg-slate-700 text-white text-[10px] font-bold">Sinh viên</span>
                                        </div>
                                    </div>
                                </div>
                                <ChevronRightIcon className="h-5 w-5 text-violet-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                            </div>
                        </button>
                    </div>

                    <div className="space-y-4 pt-2">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                    <Square2StackIcon className="h-4 w-4 text-slate-600" />
                                </span>
                                Thư viện & tài nguyên
                            </h3>
                        </div>
                        <p className="text-xs text-slate-500 -mt-1">Quản lý file, ảnh, video và học liệu đã lưu — giống không gian «Thư viện của tôi» của tài khoản học sinh.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <button
                                type="button"
                                onClick={() => onNavigate(View.DesignCenter)}
                                className="text-left p-4 sm:p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-lg hover:border-teal-200 hover:-translate-y-0.5 transition-all duration-300 group"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="h-11 w-11 rounded-xl bg-violet-600 flex items-center justify-center shrink-0 text-white group-hover:scale-105 transition-transform">
                                            <SparklesIcon className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="text-sm font-black text-slate-800 group-hover:text-teal-700 transition-colors">Thiết kế thông minh AI</h4>
                                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">Tạo thiệp mời, thực đơn, bảng khen thưởng... cho gia đình.</p>
                                        </div>
                                    </div>
                                    <ChevronRightIcon className="h-5 w-5 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => onNavigate(View.DocumentLibrary)}
                                className="text-left p-4 sm:p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-lg hover:border-teal-200 hover:-translate-y-0.5 transition-all duration-300 group"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="h-11 w-11 rounded-xl bg-slate-600 flex items-center justify-center shrink-0 text-white group-hover:scale-105 transition-transform">
                                            <Square2StackIcon className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="text-sm font-black text-slate-800 group-hover:text-teal-700 transition-colors">Thư viện của tôi</h4>
                                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">Tải lên, xem và tổ chức tài liệu, ảnh, video cho gia đình.</p>
                                        </div>
                                    </div>
                                    <ChevronRightIcon className="h-5 w-5 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => onNavigate(View.SharedLibrary)}
                                className="text-left p-4 sm:p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-lg hover:border-teal-200 hover:-translate-y-0.5 transition-all duration-300 group"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="h-11 w-11 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0 text-white group-hover:scale-105 transition-transform">
                                            <DocumentDuplicateIcon className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="text-sm font-black text-slate-800 group-hover:text-teal-700 transition-colors">Thư viện cộng đồng</h4>
                                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">Khám phá và sao chép học liệu được chia sẻ.</p>
                                        </div>
                                    </div>
                                    <ChevronRightIcon className="h-5 w-5 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column: Alerts & Teacher Connection */}
                <div className="space-y-6">
                    <div className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <ClockIcon className="h-4 w-4 text-amber-600" />
                        </span>
                        <h3 className="text-base font-bold text-slate-800">Thông báo từ lớp</h3>
                    </div>
                    
                    <div className="space-y-3">
                        {classNotifications.length === 0 ? (
                            <div className="p-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                <ClockIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                <p className="text-xs text-slate-500 font-medium">Chưa có thông báo từ lớp</p>
                                {children.length > 0 && assignments.length === 0 && (
                                    <p className="text-[11px] text-amber-600 mt-2 font-medium">Đảm bảo con đã được duyệt vào lớp. Nhấn "Làm mới" sau khi giáo viên phê duyệt.</p>
                                )}
                            </div>
                        ) : (
                            <>
                                {classNotifications.slice(0, notifLimit).map((item, i) => {
                                    const timeAgo = (() => {
                                        const d = item.date ? new Date(item.date).getTime() : 0;
                                        const s = Math.floor((Date.now() - d) / 1000);
                                        if (s < 60) return 'Vừa xong';
                                        if (s < 3600) return Math.floor(s / 60) + ' phút trước';
                                        if (s < 86400) return Math.floor(s / 3600) + ' giờ trước';
                                        if (s < 604800) return Math.floor(s / 86400) + ' ngày trước';
                                        return new Date(item.date).toLocaleDateString('vi-VN');
                                    })();
                                    const isGraded = item.type === 'graded';
                                    const isGroup = item.type === 'group';
                                    const isNotification = item.type === 'notification';
                                    
                                    return (
                                        <button
                                            type="button"
                                            key={i}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isGroup) {
                                                    onNavigate(View.ChildDashboard, { child: item.child, focusSection: item.items?.[0]?.type === 'graded' ? 'progress' : 'homework' });
                                                } else if (item.notification?.link) {
                                                    onNavigate(item.notification.link.view as View, item.notification.link.payload);
                                                } else {
                                                    onNavigate(View.ChildDashboard, { child: item.child, assignmentId: item.assignment?.id });
                                                }
                                            }}
                                            className={`w-full text-left p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-teal-100 transition-all cursor-pointer group/notif ${isGroup ? 'ring-2 ring-teal-500/5' : ''}`}
                                        >
                                            <div className="flex gap-3">
                                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isGraded ? 'bg-sky-50 text-sky-600' : isNotification ? 'bg-teal-50 text-teal-600' : isGroup ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                                    {isGraded ? <CheckBadgeIcon className="h-4 w-4" /> : isGroup ? <ClipboardDocumentCheckIcon className="h-4 w-4" /> : <ClipboardDocumentListIcon className="h-4 w-4" />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-xs font-semibold">
                                                            <UserCircleIcon className="h-4 w-4" />
                                                            {item.child.name}
                                                        </span>
                                                        {item.class && (
                                                            <span className="text-xs text-slate-500">{item.class.name}</span>
                                                        )}
                                                    </div>
                                                    <p className={`text-sm font-bold text-slate-800 truncate ${isGroup ? 'text-teal-700' : ''}`}>{item.title}</p>
                                                    <div className="flex items-center justify-between mt-1.5">
                                                        <p className="text-[10px] text-slate-400">{timeAgo}</p>
                                                        {isGroup && (
                                                            <span className="text-[9px] font-black text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded uppercase tracking-tighter">Bản tin nhóm</span>
                                                        )}
                                                    </div>
                                                    {(item.assignment || isGroup) && !isNotification && (
                                                        <span className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg bg-teal-50 text-teal-700 text-[10px] font-black border border-teal-100 group-hover/notif:bg-teal-600 group-hover/notif:text-white transition-colors uppercase tracking-widest">
                                                            {isGraded ? 'Xem kết quả →' : isGroup ? 'Xem chi tiết tất cả →' : 'Làm bài ngay →'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                                
                                {classNotifications.length > notifLimit && (
                                    <button 
                                        onClick={() => setNotifLimit(prev => prev + 8)}
                                        className="w-full py-3 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] hover:bg-slate-100 hover:text-slate-600 transition-all active:scale-95"
                                    >
                                        Xem thêm thông báo (+{classNotifications.length - notifLimit})
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 p-5 shadow-lg">
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-2">
                                <PhoneIcon className="h-5 w-5 text-teal-400" />
                                <h4 className="text-base font-bold text-white">Liên thông tin học</h4>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed mb-4">
                                Kết nối trực tiếp với giáo viên để trao đổi tình hình học tập của con.
                            </p>
                            <button onClick={() => setIsTeacherDirectoryOpen(true)} className="w-full py-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold rounded-xl transition-all active:scale-[0.98]">
                                Mở danh bạ giáo viên
                            </button>
                        </div>
                        <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-teal-500/10 rounded-full blur-xl" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParentDashboard;